import React from 'react';
import { IconButtonProps } from 'components/form-elements/buttons/IconButton';
import { Box, Flex } from 'components/layout';
import { StyleObject } from 'theming';
import * as ReactIntl from 'lib/intl';
import { Colors } from 'theming/foundations/colors';

type IconSVGProps = Omit<React.SVGAttributes<SVGElement>, 'xmlns'> & {
  sx?: StyleObject;
};

const IconSVG = ({
  width = '1em',
  children,
  sx,
  ...attributes
}: IconSVGProps) => (
  <Flex
    flexDirection="row"
    alignItems="center"
    justifyContent="center"
    sx={{
      flexShrink: 0,
      width,
      height: attributes.height || 'auto',
      ...sx,
    }}
  >
    <Box
      as="svg"
      // @ts-ignore
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={attributes.height || '100%'}
      sx={{
        fill: attributes.fill,
        stroke: attributes.stroke,
      }}
      {...attributes}
    >
      {children}
    </Box>
  </Flex>
);

export const Add = ({ width = 24 }: { width?: number }) => (
  <IconSVG viewBox="0 0 24 24" width={width} fill="currentColor">
    <path d="M0 0h24v24H0V0z" fill="none" />
    <path d="M18 13h-5v5c0 .55-.45 1-1 1s-1-.45-1-1v-5H6c-.55 0-1-.45-1-1s.45-1 1-1h5V6c0-.55.45-1 1-1s1 .45 1 1v5h5c.55 0 1 .45 1 1s-.45 1-1 1z" />
  </IconSVG>
);

export const AddButton: React.FC<IconSVGProps> = ({ width }) => (
  <IconSVG
    className="default"
    fill="none"
    stroke="white.50"
    strokeLinecap="round"
    strokeLinejoin="bevel"
    strokeWidth="4px"
    width={width}
    viewBox="0 0 31.011 31.011"
  >
    <g transform="translate(2 2)">
      <line x1="27.011" transform="translate(0 13.505)" />
      <line y1="27.011" transform="translate(13.505 0)" />
    </g>
  </IconSVG>
);

export const Star = ({ width }: IconButtonProps) => (
  <IconSVG viewBox="0 0 24 24" fill="currentColor" width={width}>
    <g>
      <path d="M0,0h24v24H0V0z" fill="none" />
      <path d="M0,0h24v24H0V0z" fill="none" />
    </g>
    <g>
      <path d="M12,17.27l4.15,2.51c0.76,0.46,1.69-0.22,1.49-1.08l-1.1-4.72l3.67-3.18c0.67-0.58,0.31-1.68-0.57-1.75l-4.83-0.41 l-1.89-4.46c-0.34-0.81-1.5-0.81-1.84,0L9.19,8.63L4.36,9.04c-0.88,0.07-1.24,1.17-0.57,1.75l3.67,3.18l-1.1,4.72 c-0.2,0.86,0.73,1.54,1.49,1.08L12,17.27z" />
    </g>
  </IconSVG>
);

export const Back: React.FC<IconSVGProps> & { fill?: string } = ({
  fill = 'currentColor',
  width = 20,
}) => (
  <IconSVG width={width} viewBox="0 0 20.243 13.501">
    <path
      className="default"
      fill={fill}
      d="M15.216,11.51a.919.919,0,0,1,.007,1.294l-4.268,4.282H27.218a.914.914,0,0,1,0,1.828H10.955L15.23,23.2a.925.925,0,0,1-.007,1.294.91.91,0,0,1-1.287-.007L8.142,18.647h0a1.026,1.026,0,0,1-.19-.288.872.872,0,0,1-.07-.352.916.916,0,0,1,.26-.64l5.794-5.836A.9.9,0,0,1,15.216,11.51Z"
      transform="translate(-7.882 -11.252)"
    />
  </IconSVG>
);

export const BurgerMenu: React.FC<IconSVGProps> = ({
  width = 24,
  fill = 'currentColor',
}) => (
  <IconSVG fill={fill} width={width} id="Layer_1" viewBox="0 0 24 24">
    <path d="M0 0h24v24H0V0z" fill="none" />
    <path d="M4 18h16c.55 0 1-.45 1-1s-.45-1-1-1H4c-.55 0-1 .45-1 1s.45 1 1 1zm0-5h16c.55 0 1-.45 1-1s-.45-1-1-1H4c-.55 0-1 .45-1 1s.45 1 1 1zM3 7c0 .55.45 1 1 1h16c.55 0 1-.45 1-1s-.45-1-1-1H4c-.55 0-1 .45-1 1z" />
  </IconSVG>
);

export const ChevronDownFilled: React.FC<IconButtonProps> = ({ width, sx }) => (
  <IconSVG
    sx={{
      width,
      fill: 'currentcolor',
      ...sx,
    }}
    viewBox="0 0 18.549 10.274"
  >
    <g transform="translate(1.414 1.414)">
      <path d="M9,13.5l7.86,7.86,7.86-7.86" transform="translate(-9 -13.5)" />
    </g>
  </IconSVG>
);

export const ChevronUpFilled = (props: IconButtonProps) => (
  <ChevronDownFilled
    {...props}
    sx={{
      ...props.sx,
      transform: 'rotate(-180deg)',
    }}
  />
);

export const ChevronDown = ({ width, color, strokeWidth }: IconButtonProps) => (
  <IconSVG
    width={width}
    stroke={color}
    color={color}
    viewBox="0 0 13.308 7.404"
  >
    <g transform="translate(1.061 1.061)">
      <path
        fill="none"
        stroke={color ?? 'currentColor'}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={strokeWidth ?? '1.5px'}
        d="M9,13.5l5.593,5.593L20.187,13.5"
        transform="translate(-9 -13.5)"
      />
    </g>
  </IconSVG>
);
export const ChevronUp = ({ width, color, strokeWidth }: IconButtonProps) => (
  <IconSVG
    width={width}
    stroke={color}
    color={color}
    viewBox="0 0 20.219 10.137"
  >
    <path
      d="M11942.571-18058.09l-6.522,8.48,6.522,7.52"
      transform="translate(-18039.975 -11934.55) rotate(90)"
      fill="none"
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={strokeWidth ?? '1.5px'}
    />
  </IconSVG>
);
export const DocumentsFilled: React.FC<IconButtonProps> = ({ width }) => (
  <IconSVG width={width} viewBox="0 0 19.506 21.701" fill="currentColor">
    <g transform="translate(-4626.444 553.331)">
      <path
        className="a"
        d="M4640.325-541.731l-.592.02-2.955.1-.035,0h-.131l-.039,0a1.706,1.706,0,0,1-1.619-1.769l.1-3,.02-.591.419-.419,5.939-5.94h-9.038a2.753,2.753,0,0,0-2.7,2.23h-.5a2.753,2.753,0,0,0-2.75,2.75v13.971a2.753,2.753,0,0,0,2.75,2.75H4639.4a2.753,2.753,0,0,0,2.75-2.75v-.451h.46a2.752,2.752,0,0,0,2.75-2.75v-9.188l-4.619,4.619Zm.328,7.351a1.251,1.251,0,0,1-1.25,1.25h-10.209a1.251,1.251,0,0,1-1.25-1.25v-13.971a1.251,1.251,0,0,1,1.25-1.25h.45v12.02a2.752,2.752,0,0,0,2.75,2.75h8.259Z"
      />
      <path
        className="a"
        d="M4645.673-550.581l-1.7-1.75a1,1,0,0,0-1.414-.005l-.006.005-6,6-.1,3a.21.21,0,0,0,.2.221h.031l3-.1,6-6A1,1,0,0,0,4645.673-550.581Z"
      />
    </g>
  </IconSVG>
);

export const Documents: React.FC<IconButtonProps> = ({ width, color }) => (
  <IconSVG
    width={width}
    viewBox="0 0 20.345 21.746"
    strokeWidth={1.5}
    fill="none"
    stroke={color ?? 'currentColor'}
    strokeLinecap="round"
  >
    <g className="default" transform="translate(-1.77 -1.204)">
      <path d="M19.94,11.3V17a2,2,0,0,1-2,2H7.72a2,2,0,0,1-2-2V4a2,2,0,0,1,2-2h5.9" />
      <path d="M16.73,19v1.2a2,2,0,0,1-2,2H4.52a2,2,0,0,1-2-2V6.23a2,2,0,0,1,2-2h1.2" />
      <path d="M19.3,2.25a1,1,0,0,0-1.42,0l-6,6h0l-.1,3a.21.21,0,0,0,.23.22l3-.1h0l6-6A1,1,0,0,0,21,4Z" />
    </g>
  </IconSVG>
);

export const EyeOpen: React.FC<IconButtonProps> = ({ width, sx }) => (
  <IconSVG width={width} sx={sx} viewBox="0 0 512 512">
    <circle cx="256" cy="256" r="64" />
    <path d="M490.84 238.6c-26.46-40.92-60.79-75.68-99.27-100.53C349 110.55 302 96 255.66 96c-42.52 0-84.33 12.15-124.27 36.11c-40.73 24.43-77.63 60.12-109.68 106.07a31.92 31.92 0 0 0-.64 35.54c26.41 41.33 60.4 76.14 98.28 100.65C162 402 207.9 416 255.66 416c46.71 0 93.81-14.43 136.2-41.72c38.46-24.77 72.72-59.66 99.08-100.92a32.2 32.2 0 0 0-.1-34.76zM256 352a96 96 0 1 1 96-96a96.11 96.11 0 0 1-96 96z" />
  </IconSVG>
);

export const EyeClosed: React.FC<IconButtonProps> = ({ width, sx }) => (
  <IconSVG width={width} sx={sx} viewBox="0 0 512 512">
    <path d="M256.1 144.8c56.2 0 101.9 45.3 101.9 101.1 0 13.1-2.6 25.5-7.3 37l59.5 59c30.8-25.5 55-58.4 69.9-96-35.3-88.7-122.3-151.6-224.2-151.6-28.5 0-55.8 5.1-81.1 14.1l44 43.7c11.6-4.6 24.1-7.3 37.3-7.3zM52.4 89.7l46.5 46.1 9.4 9.3c-33.9 26-60.4 60.8-76.3 100.8 35.2 88.7 122.2 151.6 224.1 151.6 31.6 0 61.7-6.1 89.2-17l8.6 8.5 59.7 59 25.9-25.7L78.2 64 52.4 89.7zM165 201.4l31.6 31.3c-1 4.2-1.6 8.7-1.6 13.1 0 33.5 27.3 60.6 61.1 60.6 4.5 0 9-.6 13.2-1.6l31.6 31.3c-13.6 6.7-28.7 10.7-44.8 10.7-56.2 0-101.9-45.3-101.9-101.1 0-15.8 4.1-30.7 10.8-44.3zm87.8-15.7l64.2 63.7.4-3.2c0-33.5-27.3-60.6-61.1-60.6l-3.5.1z" />
  </IconSVG>
);

export const HomeFilled: React.FC<IconButtonProps> = ({ width }) => (
  <IconSVG width={width} viewBox="0 0 21.965 20.591" fill="currentColor">
    <g className="active" transform="translate(-61.088 -129.91)">
      <path d="M82.368,140.547h0Z" />
      <path d="M82.852,139.375l-1.715-1.715,0,0-6.82-6.82a3.179,3.179,0,0,0-4.489,0l-8.537,8.537a.687.687,0,1,0,.972.971l.544-.544v8.725a1.976,1.976,0,0,0,1.973,1.973H79.364a1.975,1.975,0,0,0,1.973-1.973V139.8l.546.546a.686.686,0,1,0,.969-.972Zm-8.717,9.752H70.017v-5.4a.774.774,0,0,1,.772-.772h2.574a.773.773,0,0,1,.772.772Z" />
    </g>
  </IconSVG>
);

export const Home: React.FC<IconButtonProps> = ({ width, color }) => (
  <IconSVG
    width={width}
    viewBox="0 0 21.965 20.591"
    fill={color ?? 'currentColor'}
  >
    <g className="default" transform="translate(0 -16.017)">
      <path
        d="M56.559,205.005H41.973A1.976,1.976,0,0,1,40,203.032V192.649a.686.686,0,1,1,1.373,0v10.383a.6.6,0,0,0,.6.6H56.559a.6.6,0,0,0,.6-.6V192.649a.686.686,0,1,1,1.373,0v10.383A1.976,1.976,0,0,1,56.559,205.005Z"
        transform="translate(-38.284 -168.398)"
      />
      <path
        d="M21.279,26.654a.684.684,0,0,1-.485-.2l-8.537-8.537a1.8,1.8,0,0,0-2.548,0L1.172,26.453A.686.686,0,0,1,.2,25.482l8.537-8.537a3.178,3.178,0,0,1,4.489,0l8.537,8.537a.686.686,0,0,1-.485,1.172Z"
        transform="translate(0 0)"
      />
      <path
        d="M182.178,296.906h-5.491a.686.686,0,0,1-.686-.686v-6.092a2.147,2.147,0,0,1,2.145-2.145h2.574a2.147,2.147,0,0,1,2.145,2.145v6.092A.686.686,0,0,1,182.178,296.906Zm-4.8-1.373h4.118v-5.405a.773.773,0,0,0-.772-.772h-2.574a.773.773,0,0,0-.772.772Z"
        transform="translate(-168.449 -260.299)"
      />
    </g>
  </IconSVG>
);

export const Search: React.FC<IconButtonProps & { stroke?: string }> = ({
  width,
  stroke = 'white.50',
}) => (
  <IconSVG
    width={width}
    viewBox="0 0 20.489 20.489"
    sx={{
      fill: 'none',
      stroke,
      strokeWidth: '1.5px',
    }}
  >
    <g className="default" transform="translate(0.75 0.75)">
      <path
        d="M21.192,12.846A8.346,8.346,0,1,1,12.846,4.5,8.346,8.346,0,0,1,21.192,12.846Z"
        transform="translate(-4.5 -4.5)"
      />
      <path d="M29.3,29.3l-4.32-4.32" transform="translate(-10.617 -10.617)" />
    </g>
  </IconSVG>
);

export const Users: React.FC<IconButtonProps> = ({ width, color }) => (
  <IconSVG
    width={width}
    viewBox="0 0 23.839 19.763"
    fill={color ?? 'currentColor'}
    stroke={color ?? 'currentColor'}
    strokeWidth="0.4px"
  >
    <g className="default" transform="translate(0.2 0.2)">
      <g transform="translate(0 0)">
        <path
          d="M11.684,52.167a5.1,5.1,0,1,0-5.024,0A8.58,8.58,0,0,0,0,60.268v1.251a.51.51,0,1,0,1.019,0V60.268c0-4.085,3.657-7.411,8.153-7.411s8.153,3.325,8.153,7.411v1.251a.51.51,0,1,0,1.019,0V60.268A8.579,8.579,0,0,0,11.684,52.167ZM5.1,47.761a4.076,4.076,0,1,1,4.076,4.076A4.08,4.08,0,0,1,5.1,47.761Z"
          transform="translate(0 -42.666)"
        />
        <path
          d="M333.416,92.81a4.072,4.072,0,0,0-2.24-7.476.51.51,0,1,0,0,1.019,3.057,3.057,0,0,1,0,6.114.51.51,0,1,0,0,1.019,5.9,5.9,0,0,1,6.114,5.651v.973a.51.51,0,0,0,1.019,0v-.973A6.736,6.736,0,0,0,333.416,92.81Z"
          transform="translate(-314.871 -83.296)"
        />
      </g>
    </g>
  </IconSVG>
);

export const Edit: React.FC<IconButtonProps> = ({ width, color }) => (
  <IconSVG width={width} viewBox="0 0 17.5 17.04" fill={color}>
    <g transform="translate(-380.768 -192.063)">
      <path
        className="a"
        fill="none"
        stroke="#8686a1"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5px"
        d="M382.418,204.759l11.194-11.231a1.9,1.9,0,1,1,2.687,2.7h0l-11.2,11.227-3.583.9Z"
      />
      <line
        className="a"
        fill="none"
        stroke="#8686a1"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5px"
        x2="7.938"
        transform="translate(389.581 208.353)"
      />
    </g>
  </IconSVG>
);

export const Logout: React.FC<IconButtonProps> = ({ width, color }) => (
  <IconSVG
    width={width}
    viewBox="0 0 21.193 21.193"
    fill={color}
    color={color}
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="1.5px"
  >
    <path
      className="a"
      d="M286.383,209.333h-8.389a.662.662,0,1,1,0-1.325h8.389a.662.662,0,1,1,0,1.325Zm0,0"
      transform="translate(-265.853 -199.398)"
    />
    <path
      className="a"
      d="M400.666,135.919a.663.663,0,0,1-.468-1.131l2.843-2.843L400.2,129.1a.662.662,0,1,1,.937-.937l3.311,3.311a.662.662,0,0,1,0,.937l-3.311,3.311A.658.658,0,0,1,400.666,135.919Zm0,0"
      transform="translate(-383.447 -122.673)"
    />
    <path
      className="a"
      d="M7.064,21.2a1.82,1.82,0,0,1-.547-.082L1.2,19.348A1.782,1.782,0,0,1,0,17.669V1.774A1.768,1.768,0,0,1,1.766.008,1.821,1.821,0,0,1,2.313.09l5.314,1.77A1.782,1.782,0,0,1,8.83,3.54V19.435A1.768,1.768,0,0,1,7.064,21.2ZM1.766,1.333a.443.443,0,0,0-.442.442V17.669a.459.459,0,0,0,.306.426L6.92,19.857a.48.48,0,0,0,.144.019.443.443,0,0,0,.441-.442V3.54A.459.459,0,0,0,7.2,3.115L1.91,1.352a.479.479,0,0,0-.144-.019Zm0,0"
      transform="translate(0 -0.008)"
    />
    <path
      className="a"
      d="M39.03,7.072a.663.663,0,0,1-.662-.662V2.436a1.105,1.105,0,0,0-1.1-1.1H27.33a.662.662,0,1,1,0-1.325h9.934a2.43,2.43,0,0,1,2.428,2.428V6.41A.663.663,0,0,1,39.03,7.072Zm0,0"
      transform="translate(-25.564 -0.008)"
    />
    <path
      className="a"
      d="M185.527,284.4h-3.532a.662.662,0,0,1,0-1.325h3.532a1.105,1.105,0,0,0,1.1-1.1V278a.662.662,0,0,1,1.325,0v3.974A2.43,2.43,0,0,1,185.527,284.4Zm0,0"
      transform="translate(-173.826 -265.861)"
    />
  </IconSVG>
);

export const Checkbox: React.FC<IconButtonProps> = ({ width }) => (
  <IconSVG fill="currentColor" width={width} viewBox="0 0 18.328 18.328">
    <path
      d="M21.3,4.5H6.027A1.526,1.526,0,0,0,4.5,6.027V21.3a1.526,1.526,0,0,0,1.527,1.527H21.3A1.526,1.526,0,0,0,22.828,21.3V6.027A1.526,1.526,0,0,0,21.3,4.5Zm.191,16.61a.383.383,0,0,1-.382.382H6.218a.383.383,0,0,1-.382-.382V6.218a.383.383,0,0,1,.382-.382H21.11a.383.383,0,0,1,.382.382Z"
      transform="translate(-4.5 -4.5)"
    />
    <path
      d="M20.728,13.127l-.84-.864a.181.181,0,0,0-.134-.057h0a.173.173,0,0,0-.134.057L13.8,18.13,11.679,16.01a.185.185,0,0,0-.267,0l-.85.85a.19.19,0,0,0,0,.272L13.235,19.8a.845.845,0,0,0,.558.272.886.886,0,0,0,.554-.263h0L20.733,13.4A.2.2,0,0,0,20.728,13.127Z"
      transform="translate(-6.428 -6.975)"
    />
  </IconSVG>
);

export const Timer: React.FC<IconButtonProps> = ({ width }) => (
  <IconSVG fill="currentColor" width={width} viewBox="0 0 17.5 20.5">
    <g transform="translate(.75 .75)">
      <g>
        <path d="M6,0h4" fill="none" />
        <path d="M10,0.8H6C5.6,0.8,5.2,0.4,5.2,0S5.6-0.8,6-0.8h4c0.4,0,0.8,0.3,0.8,0.8S10.4,0.8,10,0.8z" />
      </g>
      <g>
        <path
          d="M8,19.8c-4.8,0-8.8-3.9-8.8-8.8c0-4.8,3.9-8.8,8.8-8.8c4.8,0,8.8,3.9,8.8,8.8C16.8,15.8,12.8,19.8,8,19.8z
			 M8,3.8C4,3.8,0.8,7,0.8,11c0,4,3.3,7.2,7.2,7.2c4,0,7.2-3.3,7.2-7.2C15.2,7,12,3.8,8,3.8z"
        />
      </g>
      <g>
        <path
          d="M11,14.2c-0.1,0-0.3,0-0.4-0.1l-3-2c-0.2-0.1-0.3-0.4-0.3-0.6v-5c0-0.4,0.3-0.8,0.8-0.8s0.8,0.3,0.8,0.8v4.6
			l2.7,1.8c0.3,0.2,0.4,0.7,0.2,1C11.5,14.1,11.2,14.2,11,14.2z"
        />
      </g>
    </g>
  </IconSVG>
);

export const Close: React.FC<IconButtonProps> = ({ width, sx }) => (
  <IconSVG width={width} viewBox="0 0 24 24" sx={sx}>
    <path
      d="M5.72 5.72a.75.75 0 0 1 1.06 0L12 10.94l5.22-5.22a.75.75 0 1 1 1.06 1.06L13.06 12l5.22 5.22a.75.75 0 1 1-1.06 1.06L12
             13.06l-5.22 5.22a.75.75 0 0 1-1.06-1.06L10.94 12L5.72 6.78a.75.75 0 0 1 0-1.06z"
    />
  </IconSVG>
);

export const CloseCircled: React.FC<IconButtonProps> = ({ width, sx }) => (
  <IconSVG
    width={width}
    viewBox="0 0 33.235 33.235"
    fill="none"
    strokeLinecap="round"
    strokeWidth="1.5px"
    sx={{ stroke: 'currentColor', ...sx }}
  >
    <g transform="translate(1.061 1.061)">
      <g transform="translate(15.557 -4.243) rotate(45)">
        <path d="M25,14A11,11,0,1,1,14,3,11,11,0,0,1,25,14Z" />
        <path d="M18,12v8.8" transform="translate(-4 -2.4)" />
        <path d="M12,18h8.8" transform="translate(-2.4 -4)" />
      </g>
    </g>
  </IconSVG>
);

export const Status: React.FC<IconButtonProps> = ({ width }) => (
  <IconSVG width={width} viewBox="0 0 35 35">
    <circle className="a" cx="17.5" cy="17.5" r="17.5" />
    <g transform="translate(8.5 8.5)">
      <circle
        className="b"
        cx="7.642"
        cy="7.642"
        r="7.642"
        transform="translate(1.5 1.5)"
      />
      <circle
        className="b"
        cx="3"
        cy="3"
        r="3"
        transform="translate(6.142 6.142)"
      />
    </g>
  </IconSVG>
);

export const Assignee: React.FC<IconButtonProps> = ({ width }) => (
  <IconSVG width={width} viewBox="0 0 35 35">
    <circle className="a" cx="17.5" cy="17.5" r="17.5" />
    <g transform="translate(8 8.021)">
      <path
        className="b"
        d="M3,19.535l.6-2.171c1.967-7.16,10.408-7.16,12.375,0l.6,2.171"
        transform="translate(0 -2.467)"
      />
      <circle
        className="b"
        cx="3.768"
        cy="3.768"
        r="3.768"
        transform="translate(6.015 1.98)"
      />
    </g>
  </IconSVG>
);

export const CreatedBy: React.FC<IconButtonProps> = ({ width }) => (
  <IconSVG width={width} viewBox="0 0 35 35">
    <circle className="a" cx="17.5" cy="17.5" r="17.5" />
    <g transform="translate(-78.384 -503.044)">
      <path
        className="b"
        d="M9.068,6H4.348A1.348,1.348,0,0,0,3,7.348v9.439a1.348,1.348,0,0,0,1.348,1.348h9.439a1.348,1.348,0,0,0,1.348-1.348V12.068"
        transform="translate(86.384 509.474)"
      />
      <path
        className="b"
        d="M19.079,3.237A1.43,1.43,0,0,1,21.1,5.26l-6.4,6.4-2.7.674.674-2.7Z"
        transform="translate(81.429 511.226)"
      />
    </g>
  </IconSVG>
);
export const Created: React.FC<IconButtonProps> = ({ width }) => (
  <IconSVG width={width} viewBox="0 0 35 35">
    <circle className="a" cx="17.5" cy="17.5" r="17.5" />
    <g transform="translate(-78.384 -585.5)">
      <g transform="translate(84.884 592.5)">
        <path
          className="b"
          d="M6.007,6H16.559a1.507,1.507,0,0,1,1.507,1.507V18.059a1.507,1.507,0,0,1-1.507,1.507H6.007A1.507,1.507,0,0,1,4.5,18.059V7.507A1.507,1.507,0,0,1,6.007,6Z"
          transform="translate(0 -1.493)"
        />
        <path className="b" d="M24,3V6.015" transform="translate(-9.702)" />
        <path className="b" d="M12,3V6.015" transform="translate(-3.732)" />
        <path
          className="b"
          d="M4.5,15H18.066"
          transform="translate(0 -5.971)"
        />
      </g>
    </g>
  </IconSVG>
);

export const Camera: React.FC<IconButtonProps> = ({ width }) => (
  <IconSVG width={width} viewBox="0 0 40 40">
    <circle className="a" cx="20" cy="20" r="20" />
    <g transform="translate(11.111 13.333)">
      <g transform="translate(0 0)">
        <g transform="translate(0 0)">
          <path
            className="b"
            d="M16.79,53.57H13.267L12.5,51.58a.593.593,0,0,0-.553-.38H5.827a.593.593,0,0,0-.553.38l-.763,1.99H.988A.989.989,0,0,0,0,54.558v9.877a.989.989,0,0,0,.988.988h15.8a.989.989,0,0,0,.988-.988V54.558A.989.989,0,0,0,16.79,53.57Zm-.2,10.667H1.185V54.756H4.919a.593.593,0,0,0,.553-.38l.763-1.99h5.309l.763,1.99a.593.593,0,0,0,.553.38h3.733v9.482Z"
            transform="translate(0 -51.2)"
          />
        </g>
      </g>
      <g transform="translate(4.721 3.654)">
        <g transform="translate(0 0)">
          <path
            className="b"
            d="M140.132,156.439a4.168,4.168,0,1,0,4.168,4.168A4.173,4.173,0,0,0,140.132,156.439Zm0,7.151a2.983,2.983,0,1,1,2.983-2.983A2.986,2.986,0,0,1,140.132,163.59Z"
            transform="translate(-135.964 -156.439)"
          />
        </g>
      </g>
    </g>
  </IconSVG>
);

export const Checkmark = ({ width }: IconButtonProps) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      height="intrinsic"
      viewBox="0 0 24 24"
      width={width}
      fill="currentColor"
    >
      <path d="M0 0h24v24H0V0z" fill="none" />
      <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z" />
    </svg>
  );
};

export function CalendarToday({ width = 24 }: { width?: number }) {
  return (
    <IconSVG width={width} viewBox="0 0 24 24" fill="currentColor">
      <path d="M0 0h24v24H0z" fill="none" />
      <path d="M20 3h-1V1h-2v2H7V1H5v2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 18H4V8h16v13z" />
    </IconSVG>
  );
}

export function Schedule({ width = 24 }: { width?: number }) {
  return (
    <IconSVG width={width} viewBox="0 0 24 24" fill="currentColor">
      <path d="M0 0h24v24H0V0z" fill="none" />
      <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm-.22-13h-.06c-.4 0-.72.32-.72.72v4.72c0 .35.18.68.49.86l4.15 2.49c.34.2.78.1.98-.24.21-.34.1-.79-.25-.99l-3.87-2.3V7.72c0-.4-.32-.72-.72-.72z" />
    </IconSVG>
  );
}

export function Gps({
  width = 24,
  color = 'currentColor',
}: {
  width?: number;
  color?: string;
}) {
  return (
    <IconSVG width={width} viewBox="0 0 24 24" fill={color}>
      <path d="M0 0h24v24H0V0z" fill="none" />
      <path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3c-.46-4.17-3.77-7.48-7.94-7.94V2c0-.55-.45-1-1-1s-1 .45-1 1v1.06C6.83 3.52 3.52 6.83 3.06 11H2c-.55 0-1 .45-1 1s.45 1 1 1h1.06c.46 4.17 3.77 7.48 7.94 7.94V22c0 .55.45 1 1 1s1-.45 1-1v-1.06c4.17-.46 7.48-3.77 7.94-7.94H22c.55 0 1-.45 1-1s-.45-1-1-1h-1.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z" />{' '}
    </IconSVG>
  );
}

export function RetakeCamera({
  width = 24,
  color = '#fff',
}: {
  width?: number;
  color?: string;
}) {
  return (
    <IconSVG width={width} fill={color} viewBox="0 0 28 24">
      <path
        d="M4.75,6.5h5.5l2-2h8l2,2h5.5A2.5,2.5,0,0,1,30.25,9V26a2.5,2.5,0,0,1-2.5,2.5h-23A2.5,2.5,0,0,1,2.25,26V9A2.5,2.5,0,0,1,4.75,6.5Zm4.5,10.688h6.106l-2.8-2.819a5.1,5.1,0,0,1,3.675-1.594,5.25,5.25,0,0,1,0,10.5,5.178,5.178,0,0,1-4.888-3.344H9.494a7.023,7.023,0,1,0,1.8-6.85l-2.05-2.056v6.162Z"
        transform="translate(-2.25 -4.5)"
      />
    </IconSVG>
  );
}

export function PressureTest({
  width = 40,
  fill = '#3192f1',
}: {
  width?: number;
  fill?: string;
}) {
  return (
    <IconSVG width={width} viewBox="0 0 40 40">
      <Box
        as="circle"
        className="a"
        // @ts-expect-error ThemeUI prop
        cx="20"
        cy="20"
        r="20"
        sx={{ fill }}
      />
      <g transform="translate(5.439 5.581)">
        <path
          className="b"
          fill="#fff"
          d="M19.419,5A14.419,14.419,0,1,1,5,19.419,14.419,14.419,0,0,1,19.419,5Zm0,1.518a12.9,12.9,0,1,0,12.9,12.9A12.9,12.9,0,0,0,19.419,6.518Zm8.126,4.776a.755.755,0,0,1,0,1.073l-5.51,5.51A3.034,3.034,0,1,1,20.961,16.8l5.51-5.51a.759.759,0,0,1,1.073,0ZM19.419,17.9a1.518,1.518,0,1,0,1.518,1.518A1.518,1.518,0,0,0,19.419,17.9Zm8.968-2.6a9.831,9.831,0,0,1,.9,4.116.759.759,0,0,1-1.518,0,8.314,8.314,0,0,0-.759-3.482.759.759,0,1,1,1.379-.634ZM19.419,9.553a9.831,9.831,0,0,1,4.151.914.759.759,0,0,1-.639,1.377,8.352,8.352,0,0,0-11.86,7.576.759.759,0,1,1-1.518,0,9.866,9.866,0,0,1,9.866-9.866Z"
          transform="translate(-5 -5)"
        />
      </g>
    </IconSVG>
  );
}

export function PlayCircle({
  width = 24,
  color = '#fff',
}: {
  width?: number;
  color?: string;
}) {
  return (
    <IconSVG width={width} viewBox="0 0 24 24" fill={color}>
      <path
        className="a"
        d="M15.375,3.375a12,12,0,1,0,12,12A12,12,0,0,0,15.375,3.375ZM20.21,15.6l-7.915,4.788a.257.257,0,0,1-.387-.225V10.587a.256.256,0,0,1,.387-.225L20.21,15.15A.264.264,0,0,1,20.21,15.6Z"
        transform="translate(-3.375 -3.375)"
      />
    </IconSVG>
  );
}

export function StopCircle({
  width = 24,
  color = '#fff',
}: {
  width?: number;
  color?: string;
}) {
  return (
    <IconSVG width={width} viewBox="0 0 24 24" fill={color}>
      <path
        className="a"
        d="M-2146-1473a11.923,11.923,0,0,1-8.486-3.514A11.921,11.921,0,0,1-2158-1485a11.922,11.922,0,0,1,3.515-8.485A11.923,11.923,0,0,1-2146-1497a11.922,11.922,0,0,1,8.485,3.514A11.922,11.922,0,0,1-2134-1485a11.922,11.922,0,0,1-3.515,8.485A11.922,11.922,0,0,1-2146-1473Zm-2.923-16.923a2,2,0,0,0-2,2v5.846a2,2,0,0,0,2,2h5.845a2,2,0,0,0,2-2v-5.846a2,2,0,0,0-2-2Z"
        transform="translate(2158 1497)"
      />
    </IconSVG>
  );
}

export function PressureTestSplash({
  size = 42,
  splashColor = '#fff',
  outlineColor = '#9e9eaf',
}: {
  size?: number;
  splashColor?: string;
  outlineColor?: string;
}) {
  return (
    <IconSVG width={size} height={size} viewBox="0 0 42 42">
      <Box
        as="circle"
        id="Ellipse_10"
        data-name="Ellipse 10"
        // @ts-expect-error ThemeUI prop
        cx="19"
        cy="19"
        r="19"
        transform="translate(0 4)"
        sx={{
          fill: splashColor,
        }}
      />
      <g
        id="_45-gauge_-dashboard_-meter_-pressure_-speed_-speedometer_-traffic"
        data-name="45-gauge,-dashboard,-meter,-pressure,-speed,-speedometer,-traffic"
        transform="translate(-1 -5)"
      >
        <Box
          as="path"
          id="Shape"
          // @ts-expect-error ThemeUI prop
          d="M24,5A19,19,0,1,1,5,24,19,19,0,0,1,24,5Zm0,2A17,17,0,1,0,41,24,17,17,0,0,0,24,7Zm10.707,6.293a1,1,0,0,1,0,1.414l-7.261,7.261a4,4,0,1,1-1.414-1.414l7.261-7.261a1,1,0,0,1,1.414,0ZM24,22a2,2,0,1,0,2,2A2,2,0,0,0,24,22Zm11.818-3.424A12.955,12.955,0,0,1,37,24a1,1,0,0,1-2,0,10.955,10.955,0,0,0-1-4.589,1,1,0,0,1,1.817-.835ZM24,11a12.954,12.954,0,0,1,5.47,1.2,1,1,0,0,1-.842,1.814A11.006,11.006,0,0,0,13,24a1,1,0,0,1-2,0A13,13,0,0,1,24,11Z"
          sx={{
            fill: outlineColor,
          }}
        />
      </g>
    </IconSVG>
  );
}

export function CheckCircleFilled({
  fill = 'currentColor',
  width = 24,
}: {
  width?: number;
  fill?: string;
}) {
  return (
    <IconSVG
      sx={{
        '& > svg': {
          fill,
        },
      }}
      width={width}
      viewBox="0 0 24 24"
    >
      <path d="M0 0h24v24H0z" fill="none" />
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
    </IconSVG>
  );
}

export function CancelFilled({
  fill = 'currentColor',
  width = 24,
}: {
  width?: number;
  fill?: string;
}) {
  return (
    <IconSVG
      sx={{
        '& > svg': {
          fill,
        },
      }}
      width={width}
      viewBox="0 0 24 24"
    >
      <path d="M0 0h24v24H0z" fill="none" />
      <path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z" />
    </IconSVG>
  );
}

export function Info({
  fill = 'currentColor',
  width = 22,
}: {
  width?: number;
  fill?: string;
}) {
  return (
    <IconSVG width={width} viewBox="0 0 21.688 21.688">
      <g transform="translate(0 0)">
        <path
          fill={fill}
          d="M16.552,11.746a1.074,1.074,0,1,1,1.069,1.043A1.044,1.044,0,0,1,16.552,11.746Zm.073,1.861h1.992V21.12H16.625Z"
          transform="translate(-6.782 -5.271)"
        />
        <path
          fill={fill}
          d="M14.219,4.835A9.381,9.381,0,1,1,7.582,7.582a9.322,9.322,0,0,1,6.637-2.747m0-1.46A10.844,10.844,0,1,0,25.063,14.219,10.842,10.842,0,0,0,14.219,3.375Z"
          transform="translate(-3.375 -3.375)"
        />
      </g>
    </IconSVG>
  );
}

export function Warning({
  fill = 'currentColor',
  width = 21,
}: {
  width?: number;
  fill?: string;
}) {
  return (
    <IconSVG width={width} viewBox="0 0 21 21">
      <path
        fill={fill}
        d="M13.071,3.831l8.8,17.539H4.271l8.8-17.539Zm0-1.9a1.518,1.518,0,0,0-1.249.916L2.859,20.708c-.687,1.221-.1,2.22,1.3,2.22H21.985c1.4,0,1.985-1,1.3-2.22h0L14.319,2.844a1.518,1.518,0,0,0-1.249-.916Zm1.313,17.063a1.313,1.313,0,1,1-1.313-1.312A1.313,1.313,0,0,1,14.383,18.991Zm-1.313-2.625a1.313,1.313,0,0,1-1.313-1.312V11.116a1.313,1.313,0,0,1,2.625,0v3.938A1.313,1.313,0,0,1,13.071,16.366Z"
        transform="translate(-2.571 -1.928)"
      />
    </IconSVG>
  );
}

export function Error({
  fill = 'currentColor',
  width = 22,
}: {
  width?: number;
  fill?: string;
}) {
  return (
    <IconSVG width={width} viewBox="0 0 21.688 21.688">
      <g transform="translate(0 -0.152)">
        <path
          fill={fill}
          d="M16.625,13.607h1.992v20.1H16.625Z"
          transform="translate(15.082 -18.225) rotate(45)"
        />
        <path
          fill={fill}
          d="M14.219,4.835A9.381,9.381,0,1,1,7.582,7.582a9.322,9.322,0,0,1,6.637-2.747m0-1.46A10.844,10.844,0,1,0,25.063,14.219,10.842,10.842,0,0,0,14.219,3.375Z"
          transform="translate(-3.375 -3.223)"
        />
      </g>
    </IconSVG>
  );
}

export function Success({
  fill = 'currentColor',
  width = 22,
}: {
  width?: number;
  fill?: string;
}) {
  return (
    <IconSVG width={width} viewBox="0 0 21.781 21.781">
      <g transform="translate(-3.375 -3.375)">
        <path
          fill={fill}
          d="M21.65,13.217l-.922-.948a.2.2,0,0,0-.147-.063h0a.19.19,0,0,0-.147.063L14.047,18.7l-2.325-2.325a.2.2,0,0,0-.293,0l-.932.932a.209.209,0,0,0,0,.3l2.932,2.932a.927.927,0,0,0,.613.3.972.972,0,0,0,.607-.288h.005l7-7.037A.224.224,0,0,0,21.65,13.217Z"
          transform="translate(-1.803 -2.255)"
        />
        <path
          fill={fill}
          d="M14.266,4.841A9.421,9.421,0,1,1,7.6,7.6a9.362,9.362,0,0,1,6.665-2.759m0-1.466A10.891,10.891,0,1,0,25.156,14.266,10.889,10.889,0,0,0,14.266,3.375Z"
        />
      </g>
    </IconSVG>
  );
}

export function EditProfile({ width = 32 }: { width?: number }) {
  return (
    <IconSVG width={width} viewBox="0 0 31.682 31.92">
      <g transform="translate(0.75 0.988)">
        <path
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.5px"
          d="M16.5,6H6A3,3,0,0,0,3,9V30a3,3,0,0,0,3,3H27a3,3,0,0,0,3-3V19.5"
          transform="translate(-3 -2.818)"
        />
        <path
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.5px"
          d="M27.75,3.75a3.182,3.182,0,0,1,4.5,4.5L18,22.5,12,24l1.5-6Z"
          transform="translate(-3 -2.818)"
        />
      </g>
    </IconSVG>
  );
}

export function TimerRunning({
  width = 20,
  sx,
}: {
  width?: number;
  sx?: StyleObject;
}) {
  return (
    <IconSVG sx={sx} width={width} viewBox="0 0 19.844 19.844">
      <svg xmlns="http://www.w3.org/2000/svg">
        <path
          fill="#fff"
          d="M10.593,10.14a1.549,1.549,0,1,1-1.206-.974L11.8,6.179a.775.775,0,0,1,1.206.974ZM9.6,18.281c-.151.009-.305.014-.458.014a7.6,7.6,0,1,1,7.6-7.738.775.775,0,0,0,1.55-.028,9.147,9.147,0,0,0-8.37-8.945V.775a.775.775,0,0,0-1.55,0v.808A9.147,9.147,0,0,0,2.679,17.165a9.087,9.087,0,0,0,6.468,2.679c.183,0,.368-.005.55-.016A.775.775,0,0,0,9.6,18.281ZM14.427,1.914,16.518,3.65a.775.775,0,0,0,.99-1.193L15.417.721a.775.775,0,1,0-.99,1.193ZM1.281,3.829a.772.772,0,0,0,.489-.174L3.9,1.919a.775.775,0,0,0-.98-1.2L.791,2.453a.775.775,0,0,0,.49,1.376ZM19.844,15.193a1.371,1.371,0,0,1-.664,1.18L13.8,19.642a1.381,1.381,0,0,1-2.1-1.18V11.925a1.381,1.381,0,0,1,2.1-1.18l5.377,3.269A1.371,1.371,0,0,1,19.844,15.193Zm-1.707,0-4.882-2.968v5.936Z"
          transform="translate(0 0)"
        />
      </svg>
    </IconSVG>
  );
}

export const ThreeDots: React.FC<IconButtonProps> = ({ width }) => (
  <IconSVG width={width} viewBox="0 0 50 50" cursor="pointer">
    <defs>
      <filter id="a" x="0" y="0" filterUnits="userSpaceOnUse">
        <feOffset dy="3" />
        <feGaussianBlur stdDeviation="3" result="b" />
        <feFlood floodOpacity="0.161" />
        <feComposite operator="in" in2="b" />
        <feComposite in="SourceGraphic" />
      </filter>
    </defs>
    <g transform="translate(9 6)">
      <g filter="url(#a)" className="c" transform="matrix(1, 0, 0, 1, -9, -6)">
        <path
          fill="#fff"
          className="a"
          d="M8,0H24a8,8,0,0,1,8,8V24a8,8,0,0,1-8,8H8a8,8,0,0,1-8-8V8A8,8,0,0,1,8,0Z"
          transform="translate(9 6)"
        />
      </g>
      <g fill="#4D4D65B3" transform="translate(17.499 9.5) rotate(90)">
        <rect className="b" width="3" height="3" transform="translate(0 0)" />
        <rect className="b" width="3" height="3" transform="translate(5 0)" />
        <rect className="b" width="3" height="3" transform="translate(10 0)" />
      </g>
    </g>
  </IconSVG>
);

export function DefaultLogo() {
  return (
    <IconSVG width="43" height="43" viewBox="0 1.5 28 45">
      <text
        fill="#C2C2C2"
        fontSize="10px"
        fontWeight="500"
        transform="translate(14 11)"
      >
        <tspan x="-13.919" y="13">
          <ReactIntl.FormattedMessage id="defaultLogo.logo.label" />
        </tspan>
      </text>
    </IconSVG>
  );
}

export function Profile({ width, color = 'currentColor' }: IconButtonProps) {
  return (
    <IconSVG
      width={width}
      fill={color}
      stroke="#fff"
      strokeWidth="0.3px"
      viewBox="0 0 16.3 16.3"
    >
      <g transform="translate(0.15 0.15)">
        <path
          d="M12,5.9A2.1,2.1,0,1,1,9.9,8,2.1,2.1,0,0,1,12,5.9m0,9c2.97,0,6.1,1.46,6.1,2.1v1.1H5.9V17c0-.64,3.13-2.1,6.1-2.1M12,4a4,4,0,1,0,4,4A4,4,0,0,0,12,4Zm0,9c-2.67,0-8,1.34-8,4v3H20V17C20,14.34,14.67,13,12,13Z"
          transform="translate(-4 -4)"
        />
      </g>
    </IconSVG>
  );
}
export function EyeOpenNotFilled({ width }: IconButtonProps) {
  return (
    <IconSVG
      fill="none"
      stroke="#4d4d65"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5px"
      width={width}
      viewBox="0 0 18.1 13.573"
    >
      <g transform="translate(0.75 0.75)">
        <path
          d="M1.5,12.036S4.518,6,9.8,6s8.3,6.036,8.3,6.036-3.018,6.036-8.3,6.036S1.5,12.036,1.5,12.036Z"
          transform="translate(-1.5 -6)"
        />
        <path
          d="M18.027,15.764A2.264,2.264,0,1,1,15.764,13.5,2.264,2.264,0,0,1,18.027,15.764Z"
          transform="translate(-7.463 -9.727)"
        />
      </g>
    </IconSVG>
  );
}

export function EyeClosedNotFilled({ width }: IconButtonProps) {
  return (
    <IconSVG
      fill="none"
      stroke="#4d4d65"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5px"
      width={width}
      viewBox="0 0 18.949 18.949"
    >
      <g transform="translate(1.061 1.061)">
        <path
          d="M14.458,16.663a7.7,7.7,0,0,1-4.544,1.576c-5.354,0-8.414-6.119-8.414-6.119A14.113,14.113,0,0,1,5.37,7.576M8.308,6.184A6.976,6.976,0,0,1,9.914,6c5.354,0,8.414,6.119,8.414,6.119a14.151,14.151,0,0,1-1.652,2.44m-5.14-.818A2.295,2.295,0,1,1,8.292,10.5"
          transform="translate(-1.5 -3.705)"
        />
        <path
          className="a"
          d="M1.5,1.5,18.328,18.328"
          transform="translate(-1.5 -1.5)"
        />
      </g>
    </IconSVG>
  );
}

export const TermsAndPrivacy: React.FC<IconButtonProps> = ({
  width,
  color,
}) => (
  <IconSVG color={color} fill={color} width={width} viewBox="0 0 18.855 20.938">
    <g transform="translate(0.001 0)">
      <path
        className="a"
        d="M18.33,3.131C15.283,3.131,11.275,2.1,9.846.2A.542.542,0,0,0,9.082.124.533.533,0,0,0,9.008.2C7.578,2.1,3.57,3.131.523,3.131A.524.524,0,0,0,0,3.655v6.809c0,5.759,5,9.231,9.284,10.454a.515.515,0,0,0,.288,0c4.282-1.223,9.284-4.7,9.284-10.454V3.655a.524.524,0,0,0-.524-.524Zm-.524,7.332c0,5.113-4.48,8.247-8.38,9.406-3.9-1.159-8.38-4.293-8.38-9.406V4.17c3.027-.1,6.607-1.078,8.38-2.861,1.773,1.783,5.353,2.759,8.38,2.861v6.294Zm0,0"
        transform="translate(0)"
      />
      <path
        className="a"
        d="M154.731,155.5v1.047h-.524a.524.524,0,0,0-.524.524v4.19a.524.524,0,0,0,.524.524h5.237a.524.524,0,0,0,.524-.524v-4.19a.524.524,0,0,0-.524-.524h-.524V155.5a2.095,2.095,0,1,0-4.19,0Zm4.19,5.237h-4.19V157.6h4.19Zm-1.047-5.237v1.047h-2.095V155.5a1.047,1.047,0,0,1,2.095,0Zm0,0"
        transform="translate(-147.4 -147.132)"
      />
    </g>
  </IconSVG>
);

type AnimatedCheckmarkProps = {
  strokeColor?: string;
};
export function AnimatedCheckmark({ strokeColor }: AnimatedCheckmarkProps) {
  return (
    <Box
      as="svg"
      sx={{
        '& #circle': {
          animation: 'circle-animation 500ms linear 1 normal forwards',
        },
        '& #ejLZThXQYl04': {
          animation: 'checkmark-animation 1000ms linear 1 normal forwards',
        },

        '@keyframes checkmark-animation': {
          '0%': {
            strokeDasharray: 141.17,
            strokeDashoffset: 141.17,
          },

          '50%': {
            strokeDasharray: 141.17,
            strokeDashoffset: 141.17,
          },

          '100%': {
            strokeDasharray: 141.17,
            strokeDashoffset: 0,
          },
        },

        '@keyframes circle-animation': {
          '0%': {
            transform:
              'translate(82.687001px, 82.68997px) rotate(-90.409883deg)',
            strokeDasharray: 416.22,
            strokeDashoffset: 416.22,
          },

          '100%': {
            transform:
              'translate(82.687001px, 82.68997px) rotate(-0.000934deg)',
            strokeDasharray: 416.22,
            strokeDashoffset: 0,
          },
        },
      }}
      id="ejLZThXQYl01"
      //@ts-ignore
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 175.082000 172.586000"
      shapeRendering="geometricPrecision"
      textRendering="geometricPrecision"
    >
      <defs>
        <filter
          id="ejLZThXQYl04-filter"
          x="-400%"
          width="600%"
          y="-400%"
          height="600%"
        >
          <feGaussianBlur
            id="ejLZThXQYl04-filter-drop-shadow-0-blur"
            in="SourceAlpha"
            stdDeviation="0,0"
          />
          <feOffset
            id="ejLZThXQYl04-filter-drop-shadow-0-offset"
            dx="0"
            dy="0"
            result="tmp"
          />
          <feFlood
            id="ejLZThXQYl04-filter-drop-shadow-0-flood"
            floodColor="rgb(0,0,0)"
          />
          <feComposite
            id="ejLZThXQYl04-filter-drop-shadow-0-composite"
            operator="in"
            in2="tmp"
          />
          <feMerge id="ejLZThXQYl04-filter-drop-shadow-0-merge">
            <feMergeNode id="ejLZThXQYl04-filter-drop-shadow-0-merge-node-1" />
            <feMergeNode
              id="ejLZThXQYl04-filter-drop-shadow-0-merge-node-2"
              in="SourceGraphic"
            />
          </feMerge>
        </filter>
      </defs>
      <g
        id="ejLZThXQYl02"
        transform="matrix(1 0 0 1 3.51600000000000 3.60200000000000)"
      >
        <g
          id="circle"
          transform="translate(82.687001,82.689970) rotate(-90.409883)"
        >
          <path
            id="ejLZThXQYl03"
            d="M162.374000,75.400000L162.374000,82.731000C162.351938,118.021514,139.120590,149.094211,105.277913,159.098911C71.435236,169.103611,35.042945,155.657078,15.835947,126.051132C-3.371052,96.445186,-0.817430,57.732306,22.112029,30.905813C45.041487,4.079319,82.884719,-4.470408,115.120000,9.893000"
            transform="translate(-82.687001,-82.681164)"
            fill="none"
            stroke={strokeColor ? strokeColor : 'rgb(255,255,255)'}
            strokeWidth="13"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="1"
          />
        </g>
      </g>
      <path
        id="ejLZThXQYl04"
        d="M61.251254,78.440000L86.202817,102.288990L160.637538,25.905376"
        filter="url(#ejLZThXQYl04-filter)"
        fill="none"
        stroke={strokeColor ? strokeColor : 'rgb(255,255,255)'}
        strokeWidth="13"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeMiterlimit="1"
        strokeDashoffset="141.170000"
        strokeDasharray="141.170000"
      />
    </Box>
  );
}

export function Bell({ fill = '#fff' }: { fill?: string }) {
  return (
    <IconSVG width="19.78" height="20.684" viewBox="0 0 19.78 20.684">
      <g transform="translate(514.435 256.467)">
        <Box
          as="path"
          // @ts-ignore
          fill={fill}
          d="M-508.179-238.741H-512.7a1.725,1.725,0,0,1-1.732-1.718,1.727,1.727,0,0,1,.6-1.319.726.726,0,0,1,.079-.058,6.606,6.606,0,0,0,2.286-4.995v-2.744a6.916,6.916,0,0,1,6.791-6.892h.267a6.916,6.916,0,0,1,6.791,6.892v2.748a6.579,6.579,0,0,0,2.282,4.994.769.769,0,0,1,.079.059,1.716,1.716,0,0,1,.6,1.311v.089a1.731,1.731,0,0,1-1.729,1.637h-4.524a3.719,3.719,0,0,1-4.4,2.875,3.715,3.715,0,0,1-2.874-2.875Zm3.637,1.475a2.232,2.232,0,0,0,2.1-1.475h-4.2a2.275,2.275,0,0,0,.32.592,2.229,2.229,0,0,0,1.775.881Z"
        />
      </g>
    </IconSVG>
  );
}

export function BellActive({
  bellColor = '#fff',
  dotColor = '#fc752a',
}: {
  bellColor?: string;
  dotColor?: string;
}) {
  return (
    <IconSVG width="24.27" height="20.453" viewBox="0 0 24.27 20.453">
      <Box
        as="path"
        // @ts-ignore
        fill={bellColor}
        className="a"
        d="M1822.619,16405.8a1.737,1.737,0,0,1-.789-.191l-4.031-2.055a3.712,3.712,0,0,1-4.578.977,3.728,3.728,0,0,1-1.906-4.277l-4.031-2.055a1.731,1.731,0,0,1,.379-3.225l.037,0,.022,0a.259.259,0,0,1,.036,0,6.617,6.617,0,0,0,4.307-3.414l1.243-2.441a6.837,6.837,0,0,1,3.965-3.4,6.86,6.86,0,0,1,5.213.342l.029.012.021.012.074.037.066.033.025.014.025.016c.188.1.371.207.56.33a5.987,5.987,0,0,0-1.573.813.526.526,0,0,1-.1-.039l-.028-.018-.01-.006a.1.1,0,0,0-.019-.01,5.37,5.37,0,0,0-2.071-.412,5.485,5.485,0,0,0-4.855,2.949l-.758,1.49-.49.959a8.079,8.079,0,0,1-5.361,4.2.012.012,0,0,1-.008,0l-.008,0a.121.121,0,0,1-.037,0,.3.3,0,0,0-.111.111.25.25,0,0,0-.015.193.239.239,0,0,0,.122.141l4.628,2.359,5.291,2.7,4.628,2.355a.249.249,0,0,0,.112.025.241.241,0,0,0,.218-.133.264.264,0,0,0,.025-.16.079.079,0,0,0-.012-.021.053.053,0,0,0-.007-.016s0,0,0,0l0-.006a8.028,8.028,0,0,1,0-6.293,6.016,6.016,0,0,0,1.436.42,6.627,6.627,0,0,0-.522,3.7,6.758,6.758,0,0,0,.416,1.52.214.214,0,0,1,.029.055s0,.012.008.012h0l0,.006a.024.024,0,0,1,.005.016,1.717,1.717,0,0,1-.058,1.443l-.009.018a.521.521,0,0,1-.032.063A1.727,1.727,0,0,1,1822.619,16405.8Zm-9.934-4.848a2.475,2.475,0,0,0,.021.672,2.238,2.238,0,0,0,1.186,1.592,2.233,2.233,0,0,0,2.532-.361l-3.739-1.9Z"
        transform="translate(-1806.339 -16385.346)"
      />
      <Box
        as="circle"
        // @ts-ignore
        fill={dotColor}
        className="b"
        cx="4"
        cy="4"
        r="4"
        transform="matrix(0.891, 0.454, -0.454, 0.891, 17.142, 1.449)"
      />
    </IconSVG>
  );
}

export function Delete({ fill = 'currentColor' }: { fill?: string }) {
  return (
    <IconSVG height="24px" viewBox="0 0 24 24" width="24px" fill={fill}>
      <path d="M0 0h24v24H0V0z" fill="none" />
      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V9c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v10zM18 4h-2.5l-.71-.71c-.18-.18-.44-.29-.7-.29H9.91c-.26 0-.52.11-.7.29L8.5 4H6c-.55 0-1 .45-1 1s.45 1 1 1h12c.55 0 1-.45 1-1s-.45-1-1-1z" />
    </IconSVG>
  );
}

export function BellOutlined({ fill = '#fff' }: { fill?: string }) {
  return (
    <IconSVG
      width="19.787"
      height="20.681"
      viewBox="0 0 19.787 20.681"
      fill={fill}
    >
      <g
        id="Component_129_4"
        data-name="Component 129 – 4"
        transform="translate(0)"
      >
        <path
          id="Union_17"
          data-name="Union 17"
          d="M4062.258,18445.727h-4.524a1.725,1.725,0,0,1-1.127-3.037.889.889,0,0,1,.079-.059,6.606,6.606,0,0,0,2.286-4.994v-2.744a6.916,6.916,0,0,1,6.791-6.893h.267a6.916,6.916,0,0,1,6.791,6.893v1.6c0,.025,0,.053,0,.078v1.07a6.582,6.582,0,0,0,2.282,4.994.822.822,0,0,1,.079.059,1.719,1.719,0,0,1,.6,1.311c0,.031,0,.061,0,.09a1.73,1.73,0,0,1-1.729,1.637h-4.525a3.716,3.716,0,0,1-7.274,0Zm3.637,1.475a2.232,2.232,0,0,0,2.1-1.475H4063.8a2.272,2.272,0,0,0,.32.592A2.234,2.234,0,0,0,4065.895,18447.2Zm-8.162-2.955h16.323a.247.247,0,0,0,.247-.246.239.239,0,0,0-.051-.15.54.54,0,0,1-.043-.033,8.087,8.087,0,0,1-2.875-6.174v-2.75a5.433,5.433,0,0,0-5.014-5.4.638.638,0,0,1-.182-.01.116.116,0,0,1-.026,0c-.072,0-.145-.006-.217-.006s-.146,0-.218.006a.118.118,0,0,1-.026,0,.638.638,0,0,1-.182.01,5.436,5.436,0,0,0-5.018,5.4v2.75a8.081,8.081,0,0,1-2.871,6.174.54.54,0,0,1-.043.033.239.239,0,0,0-.051.15A.247.247,0,0,0,4057.733,18444.246Z"
          transform="translate(-4056.001 -18428)"
        />
      </g>
    </IconSVG>
  );
}

export function ArrowUpward({
  fill = '#000',
  sx,
  size = 24,
}: {
  size?: number;
  fill?: Colors | string;
  sx?: StyleObject;
}) {
  return (
    <IconSVG
      fill={fill}
      height={`${size}px`}
      viewBox="0 0 24 24"
      width={`${size}px`}
      enableBackground="new 0 0 24 24"
      sx={sx}
    >
      <rect fill="none" height="24" width="24" />
      <path d="M5,9l1.41,1.41L11,5.83V22H13V5.83l4.59,4.59L19,9l-7-7L5,9z" />
    </IconSVG>
  );
}

export function ArrowDownward({
  fill = '#000',
  size,
}: {
  size?: number;
  fill?: Colors | string;
}) {
  return (
    <ArrowUpward
      sx={{ transform: 'rotate(-180deg)' }}
      fill={fill}
      size={size}
    />
  );
}

export function LastPage({
  fill = '#000',
  sx,
  size = 24,
}: {
  size?: number;
  fill?: Colors | string;
  sx?: StyleObject;
}) {
  return (
    <IconSVG
      fill={fill}
      height={`${size}px`}
      viewBox="0 0 24 24"
      width={`${size}px`}
      sx={sx}
    >
      <path d="M0 0h24v24H0V0z" fill="none" opacity=".87" />
      <path d="M6.29 8.11L10.18 12l-3.89 3.89c-.39.39-.39 1.02 0 1.41.39.39 1.02.39 1.41 0l4.59-4.59c.39-.39.39-1.02 0-1.41L7.7 6.7c-.39-.39-1.02-.39-1.41 0-.38.39-.38 1.03 0 1.41zM17 6c.55 0 1 .45 1 1v10c0 .55-.45 1-1 1s-1-.45-1-1V7c0-.55.45-1 1-1z" />
    </IconSVG>
  );
}

export function FirstPage({
  fill = '#000',
  size,
}: {
  size?: number;
  fill?: Colors | string;
}) {
  return (
    <LastPage fill={fill} size={size} sx={{ transform: 'rotate(-180deg)' }} />
  );
}

export function NextPage({
  fill = '#000',
  sx,
  size = 24,
}: {
  size?: number;
  fill?: Colors | string;
  sx?: StyleObject;
}) {
  return (
    <IconSVG
      fill={fill}
      height={`${size}px`}
      viewBox="0 0 24 24"
      width={`${size}px`}
      sx={sx}
      enableBackground="new 0 0 24 24"
    >
      <g>
        <path d="M0,0h24v24H0V0z" fill="none" />
      </g>
      <g>
        <polygon points="6.23,20.23 8,22 18,12 8,2 6.23,3.77 14.46,12" />
      </g>
    </IconSVG>
  );
}

export function PreviousPage({
  fill = '#000',
  size,
}: {
  size?: number;
  fill?: Colors | string;
}) {
  return (
    <NextPage fill={fill} size={size} sx={{ transform: 'rotate(-180deg)' }} />
  );
}

export function Gear({
  fill = '#000',
  sx,
  size = 24,
}: {
  size?: number;
  fill?: Colors | string;
  sx?: StyleObject;
}) {
  return (
    <IconSVG
      fill={fill}
      height={`${size}px`}
      viewBox="0 0 24 24"
      width={`${size}px`}
      sx={sx}
    >
      <path d="M0 0h24v24H0V0z" fill="none" />
      <path d="M19.43 12.98c.04-.32.07-.64.07-.98 0-.34-.03-.66-.07-.98l2.11-1.65c.19-.15.24-.42.12-.64l-2-3.46c-.09-.16-.26-.25-.44-.25-.06 0-.12.01-.17.03l-2.49 1c-.52-.4-1.08-.73-1.69-.98l-.38-2.65C14.46 2.18 14.25 2 14 2h-4c-.25 0-.46.18-.49.42l-.38 2.65c-.61.25-1.17.59-1.69.98l-2.49-1c-.06-.02-.12-.03-.18-.03-.17 0-.34.09-.43.25l-2 3.46c-.13.22-.07.49.12.64l2.11 1.65c-.04.32-.07.65-.07.98 0 .33.03.66.07.98l-2.11 1.65c-.19.15-.24.42-.12.64l2 3.46c.09.16.26.25.44.25.06 0 .12-.01.17-.03l2.49-1c.52.4 1.08.73 1.69.98l.38 2.65c.03.24.24.42.49.42h4c.25 0 .46-.18.49-.42l.38-2.65c.61-.25 1.17-.59 1.69-.98l2.49 1c.06.02.12.03.18.03.17 0 .34-.09.43-.25l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.65zm-1.98-1.71c.04.31.05.52.05.73 0 .21-.02.43-.05.73l-.14 1.13.89.7 1.08.84-.7 1.21-1.27-.51-1.04-.42-.9.68c-.43.32-.84.56-1.25.73l-1.06.43-.16 1.13-.2 1.35h-1.4l-.19-1.35-.16-1.13-1.06-.43c-.43-.18-.83-.41-1.23-.71l-.91-.7-1.06.43-1.27.51-.7-1.21 1.08-.84.89-.7-.14-1.13c-.03-.31-.05-.54-.05-.74s.02-.43.05-.73l.14-1.13-.89-.7-1.08-.84.7-1.21 1.27.51 1.04.42.9-.68c.43-.32.84-.56 1.25-.73l1.06-.43.16-1.13.2-1.35h1.39l.19 1.35.16 1.13 1.06.43c.43.18.83.41 1.23.71l.91.7 1.06-.43 1.27-.51.7 1.21-1.07.85-.89.7.14 1.13zM12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 6c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z" />
    </IconSVG>
  );
}

export function Pencil({
  fill = '#000',
  sx,
  size = 24,
}: {
  size?: number;
  fill?: Colors | string;
  sx?: StyleObject;
}) {
  return (
    <IconSVG
      fill={fill}
      height={`${size}px`}
      viewBox="0 0 24 24"
      width={`${size}px`}
      sx={sx}
    >
      <path d="M0 0h24v24H0V0z" fill="none" />
      <path d="M14.06 9.02l.92.92L5.92 19H5v-.92l9.06-9.06M17.66 3c-.25 0-.51.1-.7.29l-1.83 1.83 3.75 3.75 1.83-1.83c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.2-.2-.45-.29-.71-.29zm-3.6 3.19L3 17.25V21h3.75L17.81 9.94l-3.75-3.75z" />
    </IconSVG>
  );
}

export function Remove({
  fill = '#fff',
  sx,
  size = 24,
}: {
  size?: number;
  fill?: Colors | string;
  sx?: StyleObject;
}) {
  return (
    <IconSVG
      fill={fill}
      height={`${size}px`}
      viewBox="0 0 24 24"
      width={`${size}px`}
      sx={sx}
    >
      <path d="M0 0h24v24H0V0z" fill="none" />
      <path d="M18 13H6c-.55 0-1-.45-1-1s.45-1 1-1h12c.55 0 1 .45 1 1s-.45 1-1 1z" />
    </IconSVG>
  );
}

export function ExpandLess({
  fill = '#000',
  sx,
  size = 24,
}: {
  size?: number;
  fill?: Colors | string;
  sx?: StyleObject;
}) {
  return (
    <IconSVG
      fill={fill}
      height={`${size}px`}
      viewBox="0 0 24 24"
      width={`${size}px`}
      sx={sx}
    >
      <path d="M0 0h24v24H0V0z" fill="none" />
      <path d="M12 8l-6 6 1.41 1.41L12 10.83l4.59 4.58L18 14l-6-6z" />
    </IconSVG>
  );
}

export function ImageCapture({
  fill = 'white.50',
  sx,
  size = 19,
}: {
  size?: number;
  fill?: Colors | string;
  sx?: StyleObject;
}) {
  return (
    <IconSVG
      fill={fill}
      height={`${size}px`}
      viewBox="0 0 19.998 18.001"
      width={`${size}px`}
      sx={sx}
    >
      <path
        d="M-21628,15688a2.007,2.007,0,0,1-2-2v-12a2.007,2.007,0,0,1,2-2h3.17l1.83-2h6l1.83,2h3.17a2,2,0,0,1,2,2v12a2,2,0,0,1-2,2Zm3-8a5,5,0,0,0,5,5,5,5,0,0,0,5-5,5,5,0,0,0-5-5.006A5.006,5.006,0,0,0-21625,15680Zm1.8,0a3.2,3.2,0,0,1,3.2-3.2,3.2,3.2,0,0,1,3.2,3.2,3.2,3.2,0,0,1-3.2,3.2A3.2,3.2,0,0,1-21623.2,15680Z"
        transform="translate(21630 -15669.998)"
      />
    </IconSVG>
  );
}

export function FileUpload({
  fill = 'white.50',
  sx,
  size = 20,
}: {
  size?: number;
  fill?: Colors | string;
  sx?: StyleObject;
}) {
  return (
    <IconSVG
      fill={fill}
      height={`${size}px`}
      viewBox="0 0 16 20"
      width={`${size}px`}
      sx={sx}
    >
      <path
        d="M14,2H6A2,2,0,0,0,4.01,4L4,20a2,2,0,0,0,1.99,2H18a2.006,2.006,0,0,0,2-2V8Zm4,18H6V4h7V9h5ZM8,15.01l1.41,1.41L11,14.84V19h2V14.84l1.59,1.59L16,15.01,12.01,11Z"
        transform="translate(-4 -2)"
      />
    </IconSVG>
  );
}

export function QuickCaptureSplash({
  fill = 'white.50',
  sx,
  size = 51,
}: {
  size?: number;
  fill?: Colors | string;
  sx?: StyleObject;
}) {
  return (
    <IconSVG
      fill={fill}
      height={`${size}px`}
      viewBox="0 0 51.007 50.313"
      width={`${size}px`}
      sx={sx}
    >
      <g transform="translate(-133.993 -994)">
        <g fill="#e3f1fd" transform="translate(103.699 385)">
          <ellipse
            cx="19.5"
            cy="16.5"
            rx="19.5"
            ry="16.5"
            transform="matrix(0.799, -0.602, 0.602, 0.799, 30.294, 632.958)"
          />
        </g>
        <g fill="#3192f1" transform="translate(140.482 991.158)">
          <path
            d="M7.195,18.344a.985.985,0,0,1-.985-.92A13.76,13.76,0,0,1,6.3,14.469a.986.986,0,1,1,1.951.284,11.835,11.835,0,0,0-.071,2.532.985.985,0,0,1-.918,1.048Z"
            transform="translate(4.043 10.462)"
          />
          <path
            d="M25.006,10.162a.985.985,0,0,1-.749-.345,15.415,15.415,0,0,0-3.984-3.31.985.985,0,0,1,.985-1.7,17.316,17.316,0,0,1,4.492,3.732A.985.985,0,0,1,25,10.162Z"
            transform="translate(17.287 1.794)"
          />
          <path
            d="M19.965,38.316A14.042,14.042,0,0,1,9.3,33.773a13.045,13.045,0,0,1-2.613-4.244.985.985,0,1,1,1.844-.69,11.126,11.126,0,0,0,2.228,3.621,12.111,12.111,0,0,0,9.2,3.885,17.63,17.63,0,0,0,16.8-14.58A12.454,12.454,0,0,0,34.079,11.6a12.137,12.137,0,0,0-9.406-4.118,16.853,16.853,0,0,0-10.941,4.271,17.649,17.649,0,0,0-5.02,7.136A.986.986,0,1,1,6.865,18.2a19.623,19.623,0,0,1,5.58-7.94A18.814,18.814,0,0,1,24.674,5.512a14.075,14.075,0,0,1,10.9,4.8A14.383,14.383,0,0,1,38.7,22.073,19.661,19.661,0,0,1,19.965,38.316Z"
            transform="translate(4.476 2.587)"
          />
          <path
            d="M14.106,27.75a.985.985,0,0,1,0-1.97A14.05,14.05,0,0,0,27.49,14.155a.985.985,0,0,1,1.947.3,16.083,16.083,0,0,1-15.33,13.3Z"
            transform="translate(10.79 10.192)"
          />
          <path
            d="M15.62,17.673a2.343,2.343,0,0,1-1.791-.8,2.374,2.374,0,0,1-.526-1.917,2.9,2.9,0,0,1,2.8-2.423,2.349,2.349,0,0,1,1.793.8,2.382,2.382,0,0,1,.526,1.917,2.9,2.9,0,0,1-2.8,2.423ZM16.1,14.5a.942.942,0,0,0-.855.749.426.426,0,0,0,.073.339.394.394,0,0,0,.3.114.94.94,0,0,0,.855-.749.418.418,0,0,0-.071-.339A.394.394,0,0,0,16.1,14.5Z"
            transform="translate(10.935 9.4)"
          />
          <path
            d="M15.73,17.608a.985.985,0,0,1-.646-1.73l6.722-5.832A.985.985,0,0,1,23.1,11.536l-6.722,5.832a.985.985,0,0,1-.644.24Z"
            transform="translate(12.364 6.757)"
          />
          <path
            d="M11.128,10.657a.985.985,0,0,1-.751-.349l-1.5-1.763a.985.985,0,1,1,1.5-1.273l1.5,1.773a.985.985,0,0,1-.753,1.612Z"
            transform="translate(6.437 3.943)"
          />
          <path
            d="M19.048,18.987H2.985a.985.985,0,0,1,0-1.97H19.048a.985.985,0,0,1,0,1.97Z"
            transform="translate(0 13.753)"
          />
          <path
            d="M14.694,15.6H5.176a.985.985,0,1,1,0-1.97h9.518a.985.985,0,0,1,0,1.97Z"
            transform="translate(2.126 10.464)"
          />
          <path
            d="M15.275,6.556h-9a.985.985,0,0,1,0-1.97h9a.985.985,0,1,1,0,1.97Z"
            transform="translate(3.194 1.692)"
          />
          <path
            d="M8.89,22.027H5.176a.985.985,0,1,1,0-1.97H8.89a.985.985,0,1,1,0,1.97Z"
            transform="translate(2.126 16.702)"
          />
          <path
            d="M8.338,12.96H2.985a.985.985,0,0,1,0-1.97H8.338a.985.985,0,0,1,0,1.97Z"
            transform="translate(0 7.905)"
          />
          <path
            d="M20.318,7.454h-4.4A2.09,2.09,0,0,1,13.816,5a2.579,2.579,0,0,1,2.5-2.157h4.419a2.124,2.124,0,0,1,1.616.725,2.148,2.148,0,0,1,.477,1.73,2.583,2.583,0,0,1-2.506,2.157Zm-4-2.642a.617.617,0,0,0-.55.485s-.043.187.146.187h4.4A.613.613,0,0,0,20.867,5s.032-.187-.144-.187Z"
            transform="translate(11.433 0)"
          />
        </g>
      </g>
    </IconSVG>
  );
}

export function QuickCaptureOutlined({
  color = '#fff',
  size = 24,
}: {
  color?: string;
  size?: number;
}) {
  return (
    <IconSVG viewBox="0 0 24.777 22.78" fill={color} width={size}>
      <path
        className="a"
        d="M6.782,16.449a.59.59,0,0,1-.59-.551,8.235,8.235,0,0,1,.052-1.769.59.59,0,1,1,1.167.17,7.083,7.083,0,0,0-.042,1.515.59.59,0,0,1-.549.627Z"
        transform="translate(-1.254 -0.911)"
      />
      <path
        className="a"
        d="M22.923,7.965a.59.59,0,0,1-.448-.206,9.225,9.225,0,0,0-2.384-1.981.59.59,0,0,1,.59-1.02,10.363,10.363,0,0,1,2.688,2.233.59.59,0,0,1-.447.974Z"
        transform="translate(1.191 -2.511)"
      />
      <path
        className="a"
        d="M14.6,25.142a8.4,8.4,0,0,1-6.381-2.719,7.807,7.807,0,0,1-1.563-2.54.59.59,0,1,1,1.1-.413A6.658,6.658,0,0,0,9.1,21.638,7.248,7.248,0,0,0,14.6,23.963a10.55,10.55,0,0,0,10.053-8.725,7.453,7.453,0,0,0-1.606-6.084,7.263,7.263,0,0,0-5.629-2.464,10.086,10.086,0,0,0-6.547,2.556,10.562,10.562,0,0,0-3,4.271.59.59,0,1,1-1.106-.41A11.744,11.744,0,0,1,10.1,8.354,11.259,11.259,0,0,1,17.422,5.51a8.423,8.423,0,0,1,6.523,2.871,8.607,8.607,0,0,1,1.872,7.04A11.766,11.766,0,0,1,14.6,25.142Z"
        transform="translate(-1.174 -2.364)"
      />
      <path
        className="a"
        d="M14.677,15.608a1.4,1.4,0,0,1-1.072-.48,1.421,1.421,0,0,1-.315-1.147,1.734,1.734,0,0,1,1.677-1.45,1.405,1.405,0,0,1,1.073.48,1.426,1.426,0,0,1,.315,1.147A1.734,1.734,0,0,1,14.677,15.608Zm.29-1.9a.564.564,0,0,0-.512.448.255.255,0,0,0,.044.2.236.236,0,0,0,.178.068.562.562,0,0,0,.512-.448.25.25,0,0,0-.042-.2A.236.236,0,0,0,14.967,13.71Z"
        transform="translate(0.018 -1.107)"
      />
      <path
        className="a"
        d="M15.334,14.475a.59.59,0,0,1-.387-1.035L18.97,9.95a.59.59,0,1,1,.772.891l-4.023,3.49a.59.59,0,0,1-.386.144Z"
        transform="translate(0.282 -1.595)"
      />
      <path
        className="a"
        d="M10.127,9.151a.59.59,0,0,1-.449-.209L8.783,7.887a.59.59,0,1,1,.9-.762l.895,1.061a.59.59,0,0,1-.45.964Z"
        transform="translate(-0.812 -2.114)"
      />
      <path
        className="a"
        d="M12.2,18.2H2.59a.59.59,0,0,1,0-1.179H12.2a.59.59,0,1,1,0,1.179Z"
        transform="translate(-2 -0.303)"
      />
      <path
        className="a"
        d="M10.477,14.806h-5.7a.59.59,0,0,1,0-1.179h5.7a.59.59,0,0,1,0,1.179Z"
        transform="translate(-1.608 -0.911)"
      />
      <path
        className="a"
        d="M17.694,5.6H15.06A1.251,1.251,0,0,1,13.8,4.133,1.543,1.543,0,0,1,15.3,2.842h2.645a1.271,1.271,0,0,1,.967.434,1.285,1.285,0,0,1,.285,1.035,1.546,1.546,0,0,1-1.5,1.291ZM15.3,4.021a.369.369,0,0,0-.329.29s-.026.112.087.112h2.634a.367.367,0,0,0,.329-.29s.019-.112-.086-.112Z"
        transform="translate(0.11 -2.842)"
      />
    </IconSVG>
  );
}

export function Clear({
  fill = '#000',
  sx,
  size = 24,
}: {
  size?: number;
  fill?: Colors | string;
  sx?: StyleObject;
}) {
  return (
    <IconSVG
      fill={fill}
      height={`${size}px`}
      viewBox="0 0 24 24"
      width={`${size}px`}
      sx={sx}
    >
      <path d="M0 0h24v24H0V0z" fill="none" />
      <path d="M18.3 5.71c-.39-.39-1.02-.39-1.41 0L12 10.59 7.11 5.7c-.39-.39-1.02-.39-1.41 0-.39.39-.39 1.02 0 1.41L10.59 12 5.7 16.89c-.39.39-.39 1.02 0 1.41.39.39 1.02.39 1.41 0L12 13.41l4.89 4.89c.39.39 1.02.39 1.41 0 .39-.39.39-1.02 0-1.41L13.41 12l4.89-4.89c.38-.38.38-1.02 0-1.4z" />
    </IconSVG>
  );
}

export function UserWithBackground({
  width = 24,
  fill = '#2b71d0',
}: {
  width?: number;
  fill?: string;
}) {
  return (
    <IconSVG width={width} viewBox="0 0 24 24">
      <path d="M0,0H24V24H0Z" fill="none" />
      <path
        d="M12,2A10,10,0,1,0,22,12,10,10,0,0,0,12,2Zm0,3A3,3,0,1,1,9,8,3,3,0,0,1,12,5Zm0,14.2a7.2,7.2,0,0,1-6-3.22c.03-1.99,4-3.08,6-3.08s5.97,1.09,6,3.08A7.2,7.2,0,0,1,12,19.2Z"
        fill={fill}
      />
    </IconSVG>
  );
}

export function UserWithoutBackground({ width }: { width?: number }) {
  return (
    <IconSVG fill="currentColor" width={width} viewBox="0 0 15.076 16.588">
      <path
        d="M528.163,402.531l-.6-2.17a7.984,7.984,0,0,0-4.247-5.323,4.518,4.518,0,1,0-5.332,0,7.979,7.979,0,0,0-4.241,5.319l-.6,2.171a.75.75,0,0,0,1.446.4l.6-2.171c.976-3.553,3.463-4.819,5.464-4.819s4.489,1.265,5.466,4.82l.6,2.17a.749.749,0,0,0,.722.551.732.732,0,0,0,.2-.028A.749.749,0,0,0,528.163,402.531Zm-7.515-14.138a3.018,3.018,0,1,1-3.018,3.018A3.022,3.022,0,0,1,520.648,388.393Z"
        transform="translate(-513.115 -386.893)"
      />
    </IconSVG>
  );
}

export function Card({
  width,
  color = 'currentColor',
}: {
  width?: number;
  color?: string;
}) {
  return (
    <IconSVG fill={color} width={width} viewBox="0 0 22.5 16.5">
      <g transform="translate(-515.229 -383.763)">
        <path d="M533.979,383.763h-15a3.754,3.754,0,0,0-3.75,3.75v9a3.754,3.754,0,0,0,3.75,3.75h15a3.754,3.754,0,0,0,3.75-3.75v-9A3.754,3.754,0,0,0,533.979,383.763Zm-15,1.5h15a2.252,2.252,0,0,1,2.25,2.25v.25h-19.5v-.25A2.252,2.252,0,0,1,518.979,385.263Zm15,13.5h-15a2.252,2.252,0,0,1-2.25-2.25v-7.25h19.5v7.25A2.252,2.252,0,0,1,533.979,398.763Z" />
        <path d="M520.979,393.763h-1a.75.75,0,0,0,0,1.5h1a.75.75,0,0,0,0-1.5Z" />
        <path d="M529.979,393.763h-6a.75.75,0,0,0,0,1.5h6a.75.75,0,0,0,0-1.5Z" />
      </g>
    </IconSVG>
  );
}

export function RemoveUser({ width }: { width?: number }) {
  return (
    <IconSVG
      width={width}
      viewBox="0 0 46 46"
      strokeLinecap="round"
      strokeWidth="1.8"
    >
      <defs>
        <filter
          id="Path_11660"
          x="0"
          y="0"
          width={width}
          filterUnits="userSpaceOnUse"
        >
          <feOffset dy="3" />
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feFlood flood-opacity="0.161" />
          <feComposite operator="in" in2="blur" />
          <feComposite in="SourceGraphic" />
        </filter>
      </defs>
      <g transform="translate(9 6)">
        <g transform="matrix(1, 0, 0, 1, -9, -6)" filter="url(#Path_11660)">
          <path
            d="M7,0H21a7,7,0,0,1,7,7V21a7,7,0,0,1-7,7H7a7,7,0,0,1-7-7V7A7,7,0,0,1,7,0Z"
            transform="translate(9 6)"
            fill="#fff"
          />
        </g>
        <g transform="translate(-332.069 -269.868)">
          <path
            d="M-13140.914-13139.719l8.471,8.469"
            transform="translate(13482.682 13419.285)"
            fill="none"
            stroke="#828293"
          />
          <path
            d="M0,0,8.469,8.469"
            transform="translate(341.768 288.037) rotate(-90)"
            fill="none"
            stroke="#828293"
          />
        </g>
      </g>
    </IconSVG>
  );
}

export function CameraMui({
  size = 24,
  color = '#000',
}: {
  size?: number;
  color?: string;
}) {
  return (
    <IconSVG
      height={`${size}px`}
      viewBox="0 0 24 24"
      width={`${size}px`}
      fill={color}
    >
      <path d="M0 0h24v24H0z" fill="none" />
      <circle cx="12" cy="12" r="3.2" />
      <path d="M9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z" />
    </IconSVG>
  );
}

export function Refresh({
  size = 24,
  color = '#000',
}: {
  size?: number;
  color?: string;
}) {
  return (
    <IconSVG
      height={`${size}px`}
      viewBox="0 0 24 24"
      width={`${size}px`}
      fill={color}
      sx={{ transform: 'rotateY(180deg)' }}
    >
      <path d="M0 0h24v24H0z" fill="none" />
      <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
    </IconSVG>
  );
}

export function Stop({
  size = 24,
  color = '#000',
}: {
  size?: number;
  color?: string;
}) {
  return (
    <IconSVG
      height={`${size}px`}
      viewBox="0 0 24 24"
      width={`${size}px`}
      fill={color}
    >
      <path d="M0 0h24v24H0z" fill="none" />
      <path d="M6 6h12v12H6z" />
    </IconSVG>
  );
}

export function ReliningSplash({
  size = 43,
  color = '#3192f1',
  colorBackground = '#e3f1fd',
}: {
  size?: number;
  color?: string;
  colorBackground?: string;
}) {
  return (
    <IconSVG
      height={`${size}px`}
      viewBox="0 0 43.261 43.012"
      width={`${size}px`}
    >
      <g
        id="Group_11183"
        data-name="Group 11183"
        transform="translate(-399 -939.988)"
      >
        <g
          id="Group_11149"
          data-name="Group 11149"
          transform="translate(362 332)"
        >
          <Box
            as="circle"
            id="Ellipse_218"
            data-name="Ellipse 218"
            // @ts-expect-error cast error
            cx="19"
            cy="19"
            r="19"
            transform="translate(37 613)"
            fill={colorBackground}
          />
        </g>
        <g
          id="Group_11157"
          data-name="Group 11157"
          transform="translate(3061.537 570.356)"
        >
          <g
            id="Group_11156"
            data-name="Group 11156"
            transform="translate(-2654.248 371.632)"
          >
            <g
              id="Path_11297"
              data-name="Path 11297"
              transform="translate(2654.248 -371.632)"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path
                d="M-2623.027,408.655h-7.748V394.9a1.112,1.112,0,0,0-1.019-1.184h-13.686c-4.835,0-8.768-4.569-8.768-10.185v-11.9h7.748v11.9a1.113,1.113,0,0,0,1.02,1.185h13.686c4.835,0,8.767,4.568,8.767,10.184Z"
                stroke="none"
              />
              <Box
                as="path"
                //@ts-expect-error cast
                d="M -2623.02685546875 408.6549987792969 L -2623.02685546875 394.8980102539062 C -2623.02685546875 389.2820129394531 -2626.95947265625 384.7139892578125 -2631.794189453125 384.7139892578125 L -2645.479736328125 384.7139892578125 C -2646.0419921875 384.7139892578125 -2646.5 384.1830139160156 -2646.5 383.5289916992188 L -2646.5 371.6319885253906 L -2654.248046875 371.6319885253906 L -2654.248046875 383.5289916992188 C -2654.248046875 389.1449890136719 -2650.314453125 393.7139892578125 -2645.479736328125 393.7139892578125 L -2631.794189453125 393.7139892578125 C -2631.23193359375 393.7139892578125 -2630.77490234375 394.2449951171875 -2630.77490234375 394.8980102539062 L -2630.77490234375 408.6549987792969 L -2623.02685546875 408.6549987792969 M -2623.02685546875 410.6549987792969 L -2630.77490234375 410.6549987792969 C -2631.87939453125 410.6549987792969 -2632.77490234375 409.7595825195312 -2632.77490234375 408.6549987792969 L -2632.77490234375 395.7139892578125 L -2645.479736328125 395.7139892578125 C -2646.97021484375 395.7139892578125 -2648.41455078125 395.3751220703125 -2649.7724609375 394.706787109375 C -2651.059326171875 394.0735168457031 -2652.2099609375 393.1737060546875 -2653.192626953125 392.0323486328125 C -2655.162841796875 389.7437133789062 -2656.248046875 386.7238159179688 -2656.248046875 383.5289916992188 L -2656.248046875 371.6319885253906 C -2656.248046875 370.5274353027344 -2655.3525390625 369.6319885253906 -2654.248046875 369.6319885253906 L -2646.5 369.6319885253906 C -2645.3955078125 369.6319885253906 -2644.5 370.5274353027344 -2644.5 371.6319885253906 L -2644.5 382.7139892578125 L -2631.794189453125 382.7139892578125 C -2630.3037109375 382.7139892578125 -2628.859375 383.0527954101562 -2627.50146484375 383.7210083007812 C -2626.21484375 384.3542175292969 -2625.064208984375 385.25390625 -2624.081787109375 386.3951416015625 C -2622.11181640625 388.6834716796875 -2621.02685546875 391.7031860351562 -2621.02685546875 394.8980102539062 L -2621.02685546875 408.6549987792969 C -2621.02685546875 409.7595825195312 -2621.92236328125 410.6549987792969 -2623.02685546875 410.6549987792969 Z"
                stroke="none"
                fill={color}
              />
            </g>
          </g>
          <Box
            as="line"
            id="Line_167"
            data-name="Line 167"
            //@ts-expect-error cast
            x2="13.895"
            transform="translate(-2656.537 376.495)"
            fill="none"
            stroke={color}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
          />
          <Box
            as="line"
            id="Line_169"
            data-name="Line 169"
            //@ts-expect-error cast
            x2="13.895"
            transform="translate(-2634.171 402.04)"
            fill="none"
            stroke={color}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
          />
        </g>
      </g>
    </IconSVG>
  );
}

export function Thermostat({
  size = 24,
  color = '#000',
}: {
  size?: number;
  color?: string;
}) {
  return (
    <IconSVG
      height={`${size}px`}
      viewBox="0 0 24 24"
      width={`${size}px`}
      fill={color}
    >
      <path d="M0 0h24v24H0V0z" fill="none" />
      <path d="M15 13V5c0-1.66-1.34-3-3-3S9 3.34 9 5v8c-1.21.91-2 2.37-2 4 0 2.76 2.24 5 5 5s5-2.24 5-5c0-1.63-.79-3.09-2-4zm-4-8c0-.55.45-1 1-1s1 .45 1 1h-1v1h1v2h-1v1h1v2h-2V5z" />
    </IconSVG>
  );
}

export function Relining({
  width = 47,
  fill = '#e3f1fd',
}: {
  width?: number;
  fill?: string;
}) {
  return (
    <IconSVG viewBox="0 0 47 47" width={width}>
      <Box
        as="circle"
        id="Ellipse_534"
        data-name="Ellipse 534"
        // @ts-expect-error ThemeUI prop
        cx="23.5"
        cy="23.5"
        r="23.5"
        sx={{ fill }}
      />
      <g
        id="Group_11537"
        data-name="Group 11537"
        transform="translate(8.831 8.456)"
      >
        <g id="Group_11536" data-name="Group 11536" transform="translate(0 0)">
          <path
            id="Path_11691"
            data-name="Path 11691"
            d="M26.054,29.09V18.834c0-4.186-2.932-7.592-6.535-7.592H9.316a.83.83,0,0,1-.76-.883V1.491H2.78v8.869c0,4.186,2.932,7.592,6.536,7.592h10.2a.829.829,0,0,1,.76.883V29.09Zm0,1.491H20.278a1.49,1.49,0,0,1-1.491-1.491V19.443H9.316a7.2,7.2,0,0,1-3.2-.751A8.053,8.053,0,0,1,3.566,16.7a9.688,9.688,0,0,1-2.277-6.339V1.491A1.49,1.49,0,0,1,2.78,0H8.556a1.491,1.491,0,0,1,1.491,1.491V9.752h9.472a7.192,7.192,0,0,1,3.2.751A8.045,8.045,0,0,1,25.267,12.5a9.688,9.688,0,0,1,2.277,6.339V29.09a1.491,1.491,0,0,1-1.491,1.491"
            transform="translate(-0.328 0)"
            fill="#fff"
          />
          <path
            id="Path_11692"
            data-name="Path 11692"
            d="M11.1,7.353H.745a.745.745,0,0,1,0-1.491H11.1a.745.745,0,0,1,0,1.491"
            transform="translate(0 -1.492)"
            fill="#fff"
          />
        </g>
      </g>
    </IconSVG>
  );
}

export function Discount({ width = 18 }: { width?: number }) {
  return (
    <IconSVG viewBox="0 0 18.005 22.995" width={width}>
      <g id="Group_11676" data-name="Group 11676" transform="translate(-3)">
        <g id="Group_11675" data-name="Group 11675">
          <g id="Group_11673" data-name="Group 11673">
            <path
              id="Path_11806"
              data-name="Path 11806"
              d="M12.79,21,3,11.21v2a1.966,1.966,0,0,0,.59,1.41l7.79,7.79a2,2,0,0,0,2.83,0l6.21-6.21a2,2,0,0,0,0-2.83Z"
              fill="#fff"
            />
          </g>
          <g id="Group_11674" data-name="Group 11674">
            <path
              id="Path_11807"
              data-name="Path 11807"
              d="M11.38,17.41a2,2,0,0,0,2.83,0l6.21-6.21a2,2,0,0,0,0-2.83L12.63.58A2.039,2.039,0,0,0,11.21,0H5A2.006,2.006,0,0,0,3,2V8.21a1.966,1.966,0,0,0,.59,1.41ZM7.25,3A1.25,1.25,0,1,1,6,4.25,1.25,1.25,0,0,1,7.25,3Z"
              fill="#fff"
            />
          </g>
        </g>
      </g>
    </IconSVG>
  );
}

export function Plus({ size = 62 }: { size?: number }) {
  return (
    <IconSVG width={size} viewBox="0 0 62.001 62.001">
      <defs>
        <filter
          id="Ellipse_10"
          x={3.843}
          y={6}
          width={54.316}
          height={54.316}
          filterUnits="userSpaceOnUse"
        >
          <feOffset dy={3} />
          <feGaussianBlur stdDeviation={3} result="blur" />
          <feFlood floodOpacity={0.161} />
          <feComposite operator="in" in2="blur" />
          <feComposite in="SourceGraphic" />
        </filter>
      </defs>
      <g
        id="Group_11681"
        data-name="Group 11681"
        transform="translate(-154.558 0.158)"
      >
        <circle
          id="Ellipse_10-2"
          data-name="Ellipse 10"
          cx={31}
          cy={31}
          r={31}
          transform="translate(154.558 -0.158)"
          fill="#e2f9ed"
        />
      </g>
      <g
        id="Group_11682"
        data-name="Group 11682"
        transform="translate(-141.715 12.158)"
      >
        <g
          transform="matrix(1, 0, 0, 1, 141.72, -12.16)"
          filter="url(#Ellipse_10)"
        >
          <circle
            id="Ellipse_10-3"
            data-name="Ellipse 10"
            cx={18.158}
            cy={18.158}
            r={18.158}
            transform="translate(12.84 12)"
            fill="#3ddc97"
          />
        </g>
        <g id="ic-actions-add-simple" transform="translate(165.417 10.752)">
          <line
            id="Line_5"
            data-name="Line 5"
            x1={14.613}
            transform="translate(0 7.307)"
            fill="none"
            stroke="#fff"
            strokeLinecap="round"
            strokeLinejoin="bevel"
            strokeWidth={3}
          />
          <line
            id="Line_6"
            data-name="Line 6"
            y1={14.613}
            transform="translate(7.307 0)"
            fill="none"
            stroke="#fff"
            strokeLinecap="round"
            strokeLinejoin="bevel"
            strokeWidth={3}
          />
        </g>
      </g>
    </IconSVG>
  );
}

export function Addons({ width = 24 }: { width?: number }) {
  return (
    <IconSVG width={width} viewBox="0 0 25.5 25.5">
      <path
        id="Path_11680"
        data-name="Path 11680"
        d="M4.333,8.333h4A1.333,1.333,0,0,0,9.667,7V5.667a2.667,2.667,0,1,1,5.333,0V7a1.333,1.333,0,0,0,1.333,1.333h4a1.333,1.333,0,0,1,1.333,1.333v4A1.333,1.333,0,0,0,23,15h1.333a2.667,2.667,0,0,1,0,5.333H23a1.333,1.333,0,0,0-1.333,1.333v4A1.333,1.333,0,0,1,20.333,27h-4A1.333,1.333,0,0,1,15,25.667V24.333a2.667,2.667,0,0,0-5.333,0v1.333A1.333,1.333,0,0,1,8.333,27h-4A1.333,1.333,0,0,1,3,25.667v-4a1.333,1.333,0,0,1,1.333-1.333H5.667a2.667,2.667,0,1,0,0-5.333H4.333A1.333,1.333,0,0,1,3,13.667v-4A1.333,1.333,0,0,1,4.333,8.333"
        transform="translate(-2.25 -2.25)"
        fill="none"
        stroke="#012169"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </IconSVG>
  );
}

export function Cookie({ width = 41 }: { width?: number }) {
  return (
    <IconSVG data-name="Group 11730" width={width} height={width}>
      <path data-name="Path 11830" d="M0 0h41.215v41.215H0Z" fill="none" />
      <path
        data-name="Path 11831"
        d="M13.738 22.325v.017"
        fill="none"
        stroke="#ffd65f"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
      <path
        data-name="Path 11832"
        d="M20.607 29.194v.017"
        fill="none"
        stroke="#ffd65f"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
      <path
        data-name="Path 11833"
        d="M20.607 20.607v.017"
        fill="none"
        stroke="#ffd65f"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
      <path
        data-name="Path 11834"
        d="M27.477 24.042v.017"
        fill="none"
        stroke="#ffd65f"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
      <path
        data-name="Path 11835"
        d="M18.89 13.738v.017"
        fill="none"
        stroke="#ffd65f"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
      <path
        data-name="Path 11836"
        d="m22.578 5.969 4.58 1.9a6.869 6.869 0 0 0 8 10.544l.091.227a5.152 5.152 0 0 1 0 3.943 23.048 23.048 0 0 0-1.759 3.18 29.008 29.008 0 0 0-1.133 3.806 5.152 5.152 0 0 1-2.789 2.787 30.808 30.808 0 0 0-3.806 1.135 21.9 21.9 0 0 0-3.18 1.759 5.152 5.152 0 0 1-3.943 0 23.947 23.947 0 0 0-3.18-1.759 28.386 28.386 0 0 0-3.806-1.133 5.152 5.152 0 0 1-2.787-2.789 31.375 31.375 0 0 0-1.137-3.81 21.689 21.689 0 0 0-1.759-3.18 5.152 5.152 0 0 1 0-3.943 21.221 21.221 0 0 0 1.759-3.181 31.844 31.844 0 0 0 1.132-3.805 5.152 5.152 0 0 1 2.789-2.788 29.714 29.714 0 0 0 3.805-1.133 22.646 22.646 0 0 0 3.18-1.759 5.152 5.152 0 0 1 3.943 0Z"
        fill="none"
        stroke="#ffd65f"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
    </IconSVG>
  );
}

export function AddCircle({
  size = 24,
  color = '#000',
}: {
  size?: number;
  color?: string;
}) {
  return (
    <IconSVG
      height={`${size}px`}
      viewBox="0 0 24 24"
      width={`${size}px`}
      fill={color}
      cursor="pointer"
    >
      <path d="M0 0h24v24H0z" fill="none" />
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z" />
    </IconSVG>
  );
}

export function Message({ height = 20, color = '#FFF', sx }: IconSVGProps) {
  return (
    <IconSVG
      height={`${height}px`}
      viewBox="0 0 24 24"
      width={`${height}px`}
      cursor="pointer"
      fill={color}
      sx={{
        ...sx,
      }}
    >
      <path
        id="Path_12718"
        data-name="Path 12718"
        d="M20,2H4A2.006,2.006,0,0,0,2,4V22l4-4H20a2.006,2.006,0,0,0,2-2V4A2.006,2.006,0,0,0,20,2Zm0,14H6L4,18V4H20ZM7,9H9v2H7Zm4,0h2v2H11Zm4,0h2v2H15Z"
      />
    </IconSVG>
  );
}

export function Email({ height = 20, color = '#000', sx }: IconSVGProps) {
  return (
    <IconSVG
      height={`${height}px`}
      viewBox="0 0 24 24"
      width={`${height}px`}
      cursor="pointer"
      fill={color}
      sx={{
        ...sx,
      }}
    >
      <path
        id="Path_12720"
        data-name="Path 12720"
        d="M22,6a2.006,2.006,0,0,0-2-2H4A2.006,2.006,0,0,0,2,6V18a2.006,2.006,0,0,0,2,2H20a2.006,2.006,0,0,0,2-2ZM20,6l-8,5L4,6Zm0,12H4V8l8,5,8-5Z"
      />
    </IconSVG>
  );
}

export function LanguageSwitcher({
  size = 20,
  color = '#000',
}: {
  size?: number;
  color?: string;
}) {
  return (
    <IconSVG
      height={`${size}px`}
      viewBox="0 0 20 20"
      width={`${size}px`}
      fill={color}
      cursor="pointer"
    >
      <g
        fill="none"
        stroke="#8686a1"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      >
        <path d="M.75 2.75h7" />
        <path d="M5.75.75v2c0 4.418-2.239 8-5 8" />
        <path d="M1.75 6.75c0 2.144 2.952 3.908 6.7 4" />
        <path d="m8.75 17.75 4-9 4 9" />
        <path d="M15.85 15.75h-6.2" />
      </g>
    </IconSVG>
  );
}
export function LogoutNew({ size, color }: { size?: number; color?: string }) {
  return (
    <IconSVG
      height={`${size}px`}
      viewBox="0 0 20 20"
      width={`${size}px`}
      fill={color}
      cursor="pointer"
    >
      <g fill={color ? color : '#000'}>
        <path d="M12.9 14.268a.686.686 0 0 0-.684.684.882.882 0 0 1-.88.88H2.248a.882.882 0 0 1-.88-.88V2.248a.882.882 0 0 1 .88-.88h9.089a.882.882 0 0 1 .88.88.684.684 0 0 0 1.368 0A2.248 2.248 0 0 0 11.337 0H2.248A2.248 2.248 0 0 0 0 2.248v12.7a2.248 2.248 0 0 0 2.248 2.248h9.089a2.248 2.248 0 0 0 2.248-2.248.686.686 0 0 0-.685-.68Z" />
        <path d="M15.88 3.816a.68.68 0 0 0-.484-.2.668.668 0 0 0-.484.2.681.681 0 0 0 0 .967l3.186 3.137H5.57a.684.684 0 0 0 0 1.368h12.539l-3.108 3.132a.69.69 0 0 0 0 .967.707.707 0 0 0 .479.201.66.66 0 0 0 .484-.2L20 9.362a.96.96 0 0 0 0-1.427Z" />
      </g>
    </IconSVG>
  );
}

export function City({
  size = 24,
  color = '#FFF',
}: {
  size?: number;
  color?: string;
}) {
  return (
    <IconSVG
      height={`${size}px`}
      viewBox="0 0 24 24"
      width={`${size}px`}
      fill={color}
      cursor="pointer"
    >
      <path
        id="Icon"
        d="M12 9H16.8C17.9201 9 18.4802 9 18.908 9.21799C19.2843 9.40973 19.5903 9.71569 19.782 10.092C20 10.5198 20 11.0799 20 12.2V19M12 19V4.2C12 3.0799 12 2.51984 11.782 2.09202C11.5903 1.71569 11.2843 1.40973 10.908 1.21799C10.4802 1 9.9201 1 8.8 1H5.2C4.0799 1 3.51984 1 3.09202 1.21799C2.71569 1.40973 2.40973 1.71569 2.21799 2.09202C2 2.51984 2 3.0799 2 4.2V19M21 19H1M5.5 5H8.5M5.5 9H8.5M5.5 13H8.5"
        stroke="black"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </IconSVG>
  );
}

export function Address({
  size = 24,
  color = '#FFF',
}: {
  size?: number;
  color?: string;
}) {
  return (
    <IconSVG
      height={`${size}px`}
      viewBox="0 0 24 24"
      width={`${size}px`}
      fill={color}
      cursor="pointer"
    >
      <g id="marker-pin-03">
        <g id="Icon">
          <path
            d="M12 22C13 17 20 16.4183 20 10C20 5.58172 16.4183 2 12 2C7.58172 2 4 5.58172 4 10C4 16.4183 11 17 12 22Z"
            stroke="black"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z"
            stroke="black"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </g>
      </g>
    </IconSVG>
  );
}

export const FileMinus: React.FC<IconButtonProps> = ({ width }) => (
  <IconSVG width={width} viewBox="0 0 35 35">
    <circle className="a" cx="17.5" cy="17.5" r="17.5" />
    <g transform="translate(10 9) scale(0.8)">
      <path
        className="b"
        d="M17 10.9412V5.8C17 4.11984 17 3.27976 16.673 2.63803C16.3854 2.07354 15.9265 1.6146 15.362 1.32698C14.7202 1 13.8802 1 12.2 1H5.8C4.11984 1 3.27976 1 2.63803 1.32698C2.07354 1.6146 1.6146 2.07354 1.32698 2.63803C1 3.27976 1 4.11984 1 5.8V16.2C1 17.8802 1 18.7202 1.32698 19.362C1.6146 19.9265 2.07354 20.3854 2.63803 20.673C3.27976 21 4.11984 21 5.8 21H11M11 10H5M7 14H5M13 6H5M12 16H18"
        stroke="black"
        stroke-width="1"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </g>
  </IconSVG>
);

export const Building: React.FC<IconButtonProps> = ({ width }) => (
  <IconSVG width={width} viewBox="0 0 35 35">
    <circle className="a" cx="17.5" cy="17.5" r="17.5" />
    <g transform="translate(10 9) scale(0.8)">
      <path
        className="b"
        id="Icon"
        d="M12 9H16.8C17.9201 9 18.4802 9 18.908 9.21799C19.2843 9.40973 19.5903 9.71569 19.782 10.092C20 10.5198 20 11.0799 20 12.2V19M12 19V4.2C12 3.0799 12 2.51984 11.782 2.09202C11.5903 1.71569 11.2843 1.40973 10.908 1.21799C10.4802 1 9.9201 1 8.8 1H5.2C4.0799 1 3.51984 1 3.09202 1.21799C2.71569 1.40973 2.40973 1.71569 2.21799 2.09202C2 2.51984 2 3.0799 2 4.2V19M21 19H1M5.5 5H8.5M5.5 9H8.5M5.5 13H8.5"
        stroke="black"
        stroke-linecap="round"
        stroke-width="1"
        stroke-linejoin="round"
      />
    </g>
  </IconSVG>
);

export const AddressCircle: React.FC<IconButtonProps> = ({ width }) => (
  <IconSVG width={width} viewBox="0 0 35 35">
    <circle className="a" cx="17.5" cy="17.5" r="17.5" />
    <g transform="translate(9 9) scale(0.7)">
      <g id="marker-pin-03">
        <g id="Icon" className="b">
          <path
            d="M12 22C13 17 20 16.4183 20 10C20 5.58172 16.4183 2 12 2C7.58172 2 4 5.58172 4 10C4 16.4183 11 17 12 22Z"
            stroke-width="1"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z"
            stroke-width="1"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </g>
      </g>
    </g>
  </IconSVG>
);

export const EmailCircle: React.FC<IconButtonProps> = ({ width }) => (
  <IconSVG width={width} viewBox="0 0 35 35">
    <circle className="a" cx="17.5" cy="17.5" r="17.5" />
    <g transform="translate(9 9) scale(0.7)">
      <path
        id="Path_12720"
        className="b"
        data-name="Path 12720"
        d="M22,6a2.006,2.006,0,0,0-2-2H4A2.006,2.006,0,0,0,2,6V18a2.006,2.006,0,0,0,2,2H20a2.006,2.006,0,0,0,2-2ZM20,6l-8,5L4,6Zm0,12H4V8l8,5,8-5Z"
      />
    </g>
  </IconSVG>
);

export const PhoneCircle: React.FC<IconButtonProps> = ({ width }) => (
  <IconSVG width={width} viewBox="0 0 35 35">
    <circle className="a" cx="17.5" cy="17.5" r="17.5" />
    <g transform="translate(9 9) scale(0.7)">
      <g id="phone" className="b">
        <path
          id="Icon"
          d="M8.38028 8.85323C9.07627 10.3028 10.0251 11.6615 11.2266 12.8631C12.4282 14.0646 13.7869 15.0134 15.2365 15.7094C15.3612 15.7693 15.4235 15.7992 15.5024 15.8222C15.7828 15.904 16.127 15.8453 16.3644 15.6752C16.4313 15.6274 16.4884 15.5702 16.6027 15.4559C16.9523 15.1063 17.1271 14.9315 17.3029 14.8172C17.9658 14.3862 18.8204 14.3862 19.4833 14.8172C19.6591 14.9315 19.8339 15.1063 20.1835 15.4559L20.3783 15.6508C20.9098 16.1822 21.1755 16.448 21.3198 16.7333C21.6069 17.3009 21.6069 17.9712 21.3198 18.5387C21.1755 18.8241 20.9098 19.0898 20.3783 19.6213L20.2207 19.7789C19.6911 20.3085 19.4263 20.5733 19.0662 20.7756C18.6667 21 18.0462 21.1614 17.588 21.16C17.1751 21.1588 16.8928 21.0787 16.3284 20.9185C13.295 20.0575 10.4326 18.433 8.04466 16.045C5.65668 13.6571 4.03221 10.7947 3.17124 7.76131C3.01103 7.19687 2.93092 6.91464 2.9297 6.5017C2.92833 6.04347 3.08969 5.42298 3.31411 5.02348C3.51636 4.66345 3.78117 4.39863 4.3108 3.86901L4.46843 3.71138C4.99987 3.17993 5.2656 2.91421 5.55098 2.76987C6.11854 2.4828 6.7888 2.4828 7.35636 2.76987C7.64174 2.91421 7.90747 3.17993 8.43891 3.71138L8.63378 3.90625C8.98338 4.25585 9.15819 4.43065 9.27247 4.60643C9.70347 5.26932 9.70347 6.1239 9.27247 6.78679C9.15819 6.96257 8.98338 7.13738 8.63378 7.48698C8.51947 7.60129 8.46231 7.65845 8.41447 7.72526C8.24446 7.96269 8.18576 8.30695 8.26748 8.5873C8.29048 8.6662 8.32041 8.72854 8.38028 8.85323Z"
          stroke-width="1"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </g>
    </g>
  </IconSVG>
);

export function DownloadFile({
  fill = 'white.50',
  sx,
  size = 20,
}: {
  size?: number;
  fill?: Colors | string;
  sx?: StyleObject;
}) {
  return (
    <IconSVG
      fill={fill}
      height={`${size}px`}
      viewBox="0 0 24 24"
      width={`${size}px`}
      sx={sx}
    >
      <path
        d="M21 21H3M18 11L12 17M12 17L6 11M12 17V3"
        stroke="black"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </IconSVG>
  );
}

export const Building2: React.FC<IconButtonProps> = ({ width, color }) => (
  <IconSVG
    width={width}
    viewBox="0 0 36 36"
    strokeWidth={2}
    fill="none"
    stroke={color ?? 'currentColor'}
    strokeLinecap="round"
  >
    <path
      d="M11.25 16.5H6.9C6.05992 16.5 5.63988 16.5 5.31901 16.6635C5.03677 16.8073 4.8073 17.0368 4.66349 17.319C4.5 17.6399 4.5 18.0599 4.5 18.9V31.5M24.75 16.5H29.1C29.9401 16.5 30.3601 16.5 30.681 16.6635C30.9632 16.8073 31.1927 17.0368 31.3365 17.319C31.5 17.6399 31.5 18.0599 31.5 18.9V31.5M24.75 31.5V9.3C24.75 7.61984 24.75 6.77976 24.423 6.13803C24.1354 5.57354 23.6765 5.1146 23.112 4.82698C22.4702 4.5 21.6302 4.5 19.95 4.5H16.05C14.3698 4.5 13.5298 4.5 12.888 4.82698C12.3235 5.1146 11.8646 5.57354 11.577 6.13803C11.25 6.77976 11.25 7.61984 11.25 9.3V31.5M33 31.5H3M16.5 10.5H19.5M16.5 16.5H19.5M16.5 22.5H19.5"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </IconSVG>
);
